/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component, Fragment } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AccountOverview.css";
import searchIcon from "./images/noun-search-7865280.svg";
import { simpleFetch } from "../../restWrapper";
import PropTypes from "prop-types";
import { renderOrgLanguageSelector } from "../../assets/component/renderOrgLanguageSelector.js";
import RenderPaymentDetail from "./RenderPaymentDetail";
import ProjectTable from "./ProjectTable";
import MediaGalleryModal from "./MediaGalleryModal";
import { getProjectThumbStyle } from "../../assets/helper.js";
import { getProjectTitle, getPaymentProjectLabel } from "../../assets/projectTitleHelper.js";
import { QRCodeCanvas } from "qrcode.react";
import { MAIN_DOMAIN, ORG_PAYMENT_LIST_LIMIT, ORG_PAYMENT_LIST_SORT, PLATFORM, ENVIRONMENT } from "../../config";
import { NumericFormat } from "react-number-format";
import { arrayMove } from "../../assets/helper.js";

function normalizeHexColor(hex) {
  if (!hex) return "#2b91d6";
  const trimmed = String(hex).trim();
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

function getRelativeLuminance(hexColor) {
  const hex = normalizeHexColor(hexColor).replace("#", "");
  if (hex.length !== 6 && hex.length !== 3) return 0;

  const expand = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  const r = parseInt(expand.substr(0, 2), 16) / 255;
  const g = parseInt(expand.substr(2, 2), 16) / 255;
  const b = parseInt(expand.substr(4, 2), 16) / 255;

  const adjust = (channel) =>
    channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);

  const rLin = adjust(r);
  const gLin = adjust(g);
  const bLin = adjust(b);

  return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
}

function getAccessibleTextColor(hexColor) {
  const luminance = getRelativeLuminance(hexColor);
  return luminance > 0.6 ? "#0f172a" : "#ffffff";
}

class ProjectDetails extends Component {
  constructor(props) {
    super(props);
    this.onProjectClick = this.onProjectClick.bind(this);
    this.onProjectSettingClick = this.onProjectSettingClick.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
  }
  onProjectClick() {
    this.props.onProjectClick(this.props.mission);
  }
  onProjectSettingClick(event) {
    if (event) event.stopPropagation();
    this.props.onProjectSettingClick(this.props.mission);
  }
  onDragStart(event) {
    if (!this.props.isFrameEdit || !this.props.onDragStart) return;
    this.props.onDragStart(this.props.mission.id, event);
  }
  onDragOver(event) {
    if (!this.props.isFrameEdit || !this.props.onDragOver) return;
    this.props.onDragOver(this.props.index, event);
  }
  renderClickedPoint() {
    if (!this.props.mission.clicked) return;
    return <div className="AccountOverview-indicator"></div>;
  }

  render() {
    const currentAmount = parseFloat(this.props.mission.currentAmount).toFixed(
      2
    );

    const percentage =
      (this.props.mission.currentAmount / this.props.mission.targetAmount) *
      100.0;

    const languageObj = this.props.languageObj;

    const progressStyle = {
      background: "#" + this.props.progressBarColor,
    };
    if (this.props.mission.targetAmount === 0.0) {
      progressStyle.width = "100%";
    } else {
      progressStyle.width = String(percentage) + "%";
    }
    const headerBackgroundPicture = this.props.mission.headerBackgroundPicture;
    const headerImg = headerBackgroundPicture
      ? (Array.isArray(headerBackgroundPicture)
        ? headerBackgroundPicture
        : String(headerBackgroundPicture).split(","))
      : null;

    const headerPictureStyle = getProjectThumbStyle(headerImg);

    const goalText =
      this.props.mission.targetAmount > 0 ? (
        <div className="AccountOverview-goal">
          {languageObj.goal} HK
          <NumericFormat
            decimalSeparator="."
            displayType="text"
            valueIsNumericString={true}
            value={this.props.mission.targetAmount}
            allowNegative={false}
            thousandSeparator={true}
            prefix={"$"}
            fixedDecimalScale={true}
            decimalScale={2}
          />
        </div>
      ) : (
        ""
      );

    const statusIcon =
      this.props.mission.status === "open" ? (
        <div className="AccountOverview-open"></div>
      ) : this.props.mission.status === "unlisted" ? (
        <div className="AccountOverview-unlisted"></div>
      ) : (
        <div className="AccountOverview-close"></div>
      );

    const RenderArrow = () => {
      if (!this.props.isFrameEdit) return false;
      const moveUp = (e) => {
        e.stopPropagation();
        this.props.leftArrowBtn(this.props.index);
      };
      const moveDown = (e) => {
        e.stopPropagation();
        this.props.rightArrowBtn(this.props.index);
      };
      return (
        <div className="AccountOverview-order">
          <div
            className="AccountOverview-leftArrow"
            onClick={moveUp}
            role="button"
            aria-label="Move up"
          />
          <div
            className="AccountOverview-rightArrow"
            onClick={moveDown}
            role="button"
            aria-label="Move down"
          />
        </div>
      );
    };

    const frameClassName = [
      "AccountOverview-frame",
      this.props.isFrameEdit ? "AccountOverview-frame--reorder" : "",
      this.props.isDragging ? "AccountOverview-frame--dragging" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={frameClassName}
        onClick={this.onProjectClick}
        draggable={!!this.props.isFrameEdit}
        onDragStart={this.onDragStart}
        onDragOver={this.onDragOver}
        onDragEnd={this.props.onDragEnd}
      >
        <div className="AccountOverview-frameContainer">
          <div className="AccountOverview-iconsContainer">
            {this.renderClickedPoint()}
            {statusIcon}
          </div>

          <div className="AccountOverview-mission">
            {getProjectTitle(this.props.language, this.props.mission)}
          </div>

          <div
            className="AccountOverview-frameSettings"
            data-testid="accountoverview-project-settings-btn"
            onClick={this.onProjectSettingClick}
          ></div>
        </div>

        <div className="AccountOverview-picture" style={headerPictureStyle}>
          <div className="AccountOverview-Overlay"></div>

          <div className="AccountOverview-progressOuterContainer">
            <div className="AccountOverview-goalPercentage">
              <div className="AccountOverview-percentage">
                HK
                <NumericFormat
                  decimalSeparator="."
                  displayType="text"
                  valueIsNumericString={true}
                  value={currentAmount}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={"$"}
                />
              </div>
            </div>

            {!this.props.mission.targetAmount ? null : (
              <div>
                <div className="AccountOverview-progressContainer">
                  <div
                    className="AccountOverview-progress"
                    style={progressStyle}
                  ></div>
                </div>
                {goalText}
              </div>
            )}
          </div>
        </div>

        <RenderArrow />
      </div>
    );
  }
}

ProjectDetails.propTypes = {
  index: PropTypes.number,
  isFrameEdit: PropTypes.bool,
  isDragging: PropTypes.bool,
  leftArrowBtn: PropTypes.func,
  rightArrowBtn: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragEnd: PropTypes.func,
  mission: PropTypes.object,
  progressBarColor: PropTypes.string,
  onProjectSettingClick: PropTypes.func,
  onProjectClick: PropTypes.func,
  languageObj: PropTypes.object,
  language: PropTypes.string,
};

export function receiptPaymentId(search = '') {
  const value = new URLSearchParams(search).get('paymentId');
  const id = value && /^[1-9]\d*$/.test(value) ? Number(value) : null;
  return Number.isSafeInteger(id) ? id : null;
}

/**
 * Which payout notice belongs in the sidebar, if either (CP-304).
 *
 * "deferred" is an organization that never created a Connected Account. It is
 * the only one of the two states the org can act on, so it is the only one
 * that links into setup.
 *
 * "disabled" is an account Stripe has switched both capabilities off on —
 * suspended, rejected or under review. Finishing bank and KYC setup is not the
 * remedy and may not be possible, so it stays the passive notice it has always
 * been rather than an action the org cannot take.
 *
 * hasStripeAccount is null until retrieveCapability answers, and a backend
 * that predates CP-304 omits it. Both are read as "an account exists", which
 * leaves the disabled notice behaving exactly as it did before payouts could
 * be deferred.
 */
export function payoutNoticeStateOf(state) {
  if (!state) return null;
  if (state.hasStripeAccount === false) return "deferred";
  if (state.charges_enabled === false && state.payouts_enabled === false) {
    return "disabled";
  }
  return null;
}

class AccountOverview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      username: "",
      organization: {},
      organizationObj: {},
      // Whether a Stripe Connected Account exists at all. null until
      // retrieveCapability answers — an org that deferred payout setup
      // (CP-304) has no account, which is a different thing from an account
      // Stripe has disabled, and the two are told apart nowhere else.
      hasStripeAccount: null,
      balance: 0,
      missions: [],
      fetching: false,
      loadingPage: true,
      isFrameEdit: false,
      lastId: 0,
      hasMore: true,
      selectedProject: {
        paymentsList: [],
        loadingList: false,
        lastId: 0,
        hasMore: true,
        lastUpdatedAt: '',
        isFetchingMore: false,
        searchTerm: '',
      },
      paymentDetail: null,
      // Dashboard states
      dashboardPeriod: '30d', // '7d' | '30d' | 'all'
      showDashboard: false,
      visibleSeries: { all: true, payment: true, donation: true },
      chartHoverIndex: null,
      chartHoverLeft: 0,
      paymentSearchTerm: '',
      paymentStatusFilter: '',
      paymentModeFilter: '',
      projectSearchTerm: '',
      projectStatusFilter: '',
      isLoadingAllProjects: false,
      showProjectTable: false,
      showMediaGallery: false,
      projectManagerView: "cards",
      projectManagerVisible: false,
      dragProjectId: null,
      dropProjectIndex: null,
      missionsBeforeReorder: null,
      isSavingProjectOrder: false,
      paymentTransactionTypeFilter: ORG_PAYMENT_LIST_SORT === 'organizationReceiptId' ? 'donation' : '',
      paymentSortField: ORG_PAYMENT_LIST_SORT,
      paymentSortOrder: 'desc',
      paymentDateField: 'scheduledDate', // 'scheduledDate' | 'createdAt'
      paymentDateRangePreset: '', // '' | '7d' | '30d' | 'custom'
      paymentDateRangeStart: null,
      paymentDateRangeEnd: null,
      filterPanelExpanded: false,
      paymentDetailRefreshKey: 0,
      debugPanelOpen: false,
      debugActiveSection: 'organizationObj',
      // Mobile (<=750px) tabbed layout: which pane is shown.
      // 'transactions' | 'projects'. Ignored on desktop.
      mobileTab: 'transactions',
      // Tracks the <=750px breakpoint so the Projects tab can render the full
      // project manager inline (mobile) vs. the compact list + Expand-Projects
      // takeover (desktop).
      isMobile:
        typeof window !== 'undefined' && window.matchMedia
          ? window.matchMedia('(max-width: 750px)').matches
          : false,
    };
    this.fetchData = this.fetchData.bind(this);
    this.onProjectClicked = this.onProjectClicked.bind(this);
    this.onAllTransactionsClicked = this.onAllTransactionsClicked.bind(this);
    this.onPaymentClicked = this.onPaymentClicked.bind(this);
    this.closeSideSheet = this.closeSideSheet.bind(this);

    this.onProjectSettingClick = this.onProjectSettingClick.bind(this);
    this.onLogoutClick = this.onLogoutClick.bind(this);
    this.createProjectBtn = this.createProjectBtn.bind(this);
    this.orgSettingsBtn = this.orgSettingsBtn.bind(this);
    this.navigateToStripe = this.navigateToStripe.bind(this);
    this.onAddPaymentClick = this.onAddPaymentClick.bind(this);

    this.paymentListRef = React.createRef();
    this.onScrollPaymentList = this.onScrollPaymentList.bind(this);
    this.fetchMorePayments = this.fetchMorePayments.bind(this);
    this.toggleMenu = this.toggleMenu.bind(this);
    this.setDashboardPeriod = this.setDashboardPeriod.bind(this);
    this.setRightPaneView = this.setRightPaneView.bind(this);
    this.toggleSeriesVisibility = this.toggleSeriesVisibility.bind(this);
    this.handleChartMouseMove = this.handleChartMouseMove.bind(this);
    this.handleChartMouseLeave = this.handleChartMouseLeave.bind(this);
    this.handlePaymentSearchChange = this.handlePaymentSearchChange.bind(this);
    this.applyPaymentSearch = this.applyPaymentSearch.bind(this);
    this.handleProjectSearchChange = this.handleProjectSearchChange.bind(this);
    this.handleProjectStatusFilterChange = this.handleProjectStatusFilterChange.bind(this);
    this.loadAllProjects = this.loadAllProjects.bind(this);
    this.toggleProjectTable = this.toggleProjectTable.bind(this);
    this.openMediaGallery = this.openMediaGallery.bind(this);
    this.closeMediaGallery = this.closeMediaGallery.bind(this);
    this.closeProjectManager = this.closeProjectManager.bind(this);
    this.setProjectManagerView = this.setProjectManagerView.bind(this);
    this.handleProjectReorderToggle = this.handleProjectReorderToggle.bind(this);
    this.cancelProjectReorder = this.cancelProjectReorder.bind(this);
    this.leftArrowBtn = this.leftArrowBtn.bind(this);
    this.rightArrowBtn = this.rightArrowBtn.bind(this);
    this.handleProjectDragStart = this.handleProjectDragStart.bind(this);
    this.handleProjectDragOver = this.handleProjectDragOver.bind(this);
    this.handleProjectDragLeaveList = this.handleProjectDragLeaveList.bind(this);
    this.handleProjectDrop = this.handleProjectDrop.bind(this);
    this.handleProjectDragEnd = this.handleProjectDragEnd.bind(this);
    this.handleProjectStatusChange = this.handleProjectStatusChange.bind(this);
    this.handleProjectFieldSave = this.handleProjectFieldSave.bind(this);
    this.handleBulkUpdate = this.handleBulkUpdate.bind(this);
    this.handlePaymentStatusFilterChange = this.handlePaymentStatusFilterChange.bind(this);
    this.handlePaymentModeFilterChange = this.handlePaymentModeFilterChange.bind(this);
    this.handlePaymentTransactionTypeFilterChange = this.handlePaymentTransactionTypeFilterChange.bind(this);
    this.handlePaymentSortChange = this.handlePaymentSortChange.bind(this);
    this.handlePaymentSortOrderChange = this.handlePaymentSortOrderChange.bind(this);
    this.handlePaymentDateFieldChange = this.handlePaymentDateFieldChange.bind(this);
    this.handlePaymentDateRangePresetChange = this.handlePaymentDateRangePresetChange.bind(this);
    this.handlePaymentDateRangeChange = this.handlePaymentDateRangeChange.bind(this);
    this.toggleFilterPanel = this.toggleFilterPanel.bind(this);
    this.clearAllFilters = this.clearAllFilters.bind(this);
    this.setMobileTab = this.setMobileTab.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);

    // Add menuOpen state
    this.state.menuOpen = false;

    this.volumeSvgRef = React.createRef();
    this.paymentSearchDebounce = null;
    this.projectSearchDebounce = null;
    this.loadAllProjectsPromise = null;
    this.projectManagerCloseTimer = null;
    this.paymentsRequestSeq = 0;
    this.latestPaymentsRequestSeq = 0;
    this.mobileMediaQuery = null;
    // Cached GET /organizations/projectStats result (donor/txn/date stats),
    // merged onto missions as they load so the CreateGoal donor summary has
    // data regardless of whether the full list was expanded.
    this.projectStatsMap = null;
  }

  componentDidMount() {
    simpleFetch("/organizations", "GET", {}).then((res) => {
      // console.log('[AccountOverview] /organizations response:', res);
      const body = res && res.body;

      if (!body) {
        // console.log('[AccountOverview] No body in response, logging out');
        this.setState({
          loadingPage: false,
        });
        this.props.orgLogout();
        return;
      }

      this.setState({
        loadingPage: false,
      });

      if (body.organization) {
        // console.log('[AccountOverview] body.organization exists:', body.organization);
        // console.log('[AccountOverview] body.organizationObj:', body.organizationObj);
        // console.log('[AccountOverview] subDomain:', body.organizationObj?.subDomain);
        const isPermitted = body.organizationObj.organizationPermitted;
        if (isPermitted === "declined") {
          this.props.history.push({
            pathname: "/OrgDecline",
          });
          return false;
        } else if (isPermitted !== "approved") {
          this.props.history.push({
            pathname: "/OrgSetup",
          });
          return false;
        } else {
          this.setState({
            username: body.username,
            organization: body,
            organizationObj: body.organizationObj,
          }, () => {
            // Callback ensures state is updated before fetching data (React 18 fix)
            // console.log('[AccountOverview] State set, fetching data...');
            this.fetchData();
            this.fetchPaymentsData();
            // Load donor/txn stats up front so the sidebar Overview cards (and
            // the CreateGoal donor summary opened from them) have data without
            // needing to expand the full list first.
            this.loadProjectStats();
          });
        }
      } else {
        // console.log('[AccountOverview] body.organization is missing! body:', body);
        // This is the bug - if body exists but body.organization doesn't, we're stuck in loading
        this.props.orgLogout();
      }
    }).catch((error) => {
      // console.error('[AccountOverview] Error fetching /organizations:', error);
      this.setState({
        loadingPage: false,
      });
      this.props.orgLogout();
    });

    simpleFetch("/organizations/balance", "GET", {}).then((res) => {
      if (res.status !== 200) return false;
      const balance = res.body.available[0] + res.body.pending[0];
      const floorBalance = Math.floor(balance * 100) / 100;

      this.setState({
        balance: floorBalance,
      });
    });

    /* Get KYC status */
    simpleFetch("/organizations/StripeKYCStatus", "GET").then((res) => {
      if (res.status !== 200) return false;

      if (res.body.requirements) {
        if (res.body.requirements.currently_due) {
          this.setState({
            currently_due: res.body.requirements.currently_due,
          });
        }

        if (res.body.requirements.eventually_due) {
          this.setState({
            eventually_due: res.body.requirements.eventually_due,
          });
        }

        if (res.body.requirements.past_due) {
          this.setState({
            past_due: res.body.requirements.past_due,
          });
        }

        if (
          res.body.requirements.currently_due ||
          res.body.requirements.eventually_due ||
          res.body.requirements.past_due
        ) {

          if (
            res.body.requirements.currently_due.length +
              res.body.requirements.eventually_due.length +
              res.body.requirements.past_due.length >
            0
          ) {
            this.setState({
              kyc:
                res.body.requirements.currently_due.length +
                res.body.requirements.eventually_due.length +
                res.body.requirements.past_due.length,
            });
          }
        }
      }
    });

    /* Get LegacyPaymentStatus status */
    simpleFetch("/organizations/retrieveCapability", "GET").then((res) => {
      if (res.status === 200) {
        this.setState({
          ...this.state,
          ...{
            cardPaymentsStatus: res.body.cardPaymentsStatus,
            transfersStatus: res.body.transfersStatus,
            charges_enabled: res.body.charges_enabled,
            payouts_enabled: res.body.payouts_enabled,
            // Older backends do not send this. Absent means "there is an
            // account" — the state every org was in before payouts could be
            // deferred — so the disabled notice keeps its existing behaviour.
            hasStripeAccount: res.body.hasStripeAccount !== false,
          },
        });
      }
    });

    // Watch the mobile breakpoint so the Projects tab can swap between the
    // inline manager (mobile) and the compact list + takeover (desktop).
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mobileMediaQuery = window.matchMedia('(max-width: 750px)');
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.handleViewportChange);
      } else if (this.mobileMediaQuery.addListener) {
        // Safari < 14
        this.mobileMediaQuery.addListener(this.handleViewportChange);
      }
    }
  }

  handleViewportChange(event) {
    const isMobile = event.matches;
    this.setState((prev) => {
      const next = { isMobile };
      // Crossing into mobile must not leave a desktop Expand-Projects takeover
      // open — the manager renders inline on mobile, so a lingering takeover
      // flag would double-render it.
      if (isMobile && prev.showProjectTable) {
        next.showProjectTable = false;
        next.projectManagerVisible = false;
        next.isFrameEdit = false;
      }
      return next;
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location?.search !== this.props.location?.search && receiptPaymentId(this.props.location?.search)) {
      this.receiptLinkOpened = false;
      this.fetchPaymentsData(null, true, false);
    }
    if (
      prevState.missions.length !== this.state.missions.length &&
      this.state.missions.length
    ) {
      // Set selected mission & fetch payments data based on the location state
      if (
        this.props.location.state &&
        this.props.location.state.selectedMission &&
        this.props.location.state.isFetchPaymentsData
      ) {
        const newMissions = this.state.missions.map((mission) => {
          if (mission.id === this.props.location.state.selectedMission.id) {
            mission.clicked = true;
          }

          return mission;
        });

        this.setState({
          missions: newMissions,
        }, () => {
          // Callback ensures missions state is updated before fetching payments (React 18 fix)
          this.fetchPaymentsData(
            this.props.location.state.selectedMission,
            false,
            false,
            { searchTerm: (this.state.paymentSearchTerm || '').trim() }
          );
        });

        // Clear location state when refreshing page
        if (window) {
          window.history.replaceState({}, '');
        }
      }
    }

    // Add payment list scroll event listener when the component mounts
    if (this.state.selectedProject.paymentsList.length && this.paymentListRef.current) {
      if (this.paymentListRef.current) {
        this.paymentListRef.current.addEventListener('scroll', this.onScrollPaymentList);
      }
    }
  }

  componentWillUnmount() {
    // Remove scroll event listener to prevent memory leaks
    if (this.paymentListRef.current) {
      this.paymentListRef.current.removeEventListener('scroll', this.onScrollPaymentList);
    }
    if (this.paymentSearchDebounce) {
      clearTimeout(this.paymentSearchDebounce);
      this.paymentSearchDebounce = null;
    }
    if (this.projectSearchDebounce) {
      clearTimeout(this.projectSearchDebounce);
      this.projectSearchDebounce = null;
    }
    if (this.projectManagerCloseTimer) {
      clearTimeout(this.projectManagerCloseTimer);
      this.projectManagerCloseTimer = null;
    }
    if (this.mobileMediaQuery) {
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener('change', this.handleViewportChange);
      } else if (this.mobileMediaQuery.removeListener) {
        this.mobileMediaQuery.removeListener(this.handleViewportChange);
      }
      this.mobileMediaQuery = null;
    }
  }

  onPaymentClicked(data) {
    var paymentData = this.state.selectedProject;

    var items = paymentData.paymentsList;

    for (var i = 0; i < items.length; i++) {
      if (items[i].id === data.id) {
        items[i].clicked = true;
      } else {
        items[i].clicked = false;
      }
    }
    paymentData.paymentsList = items;

    this.setState({
      paymentDetail: data,
      selectedProject: paymentData,
    });
  }

  closeSideSheet() {
    const paymentData = this.state.selectedProject;
    const items = paymentData.paymentsList;

    for (let i = 0; i < items.length; i++) {
      items[i].clicked = false;
    }
    paymentData.paymentsList = items;

    this.setState({
      paymentDetail: null,
      selectedProject: paymentData,
    });
  }

  onProjectClicked(data) {
    // Ignore selection while reordering so accidental card clicks don't discard order work.
    if (this.state.isFrameEdit) return;

    // On mobile, jump to the transactions pane so the filtered results are visible.
    this.setState({ mobileTab: 'transactions' });

    this.fetchPaymentsData(
      data,
      false,
      false,
      { searchTerm: (this.state.paymentSearchTerm || '').trim() }
    );
    var items = this.state.missions;

    for (var i = 0; i < items.length; i++) {
      if (items[i].id === data.id) {
        items[i].clicked = true;
      } else {
        items[i].clicked = false;
      }
    }
    if (this.state.showProjectTable) {
      this.closeProjectManager({
        missions: items,
        paymentDetail: null,
      });
      return;
    }
    this.setState({
      missions: items,
      paymentDetail: null,
    });
  }

  onAllTransactionsClicked() {
    if (this.state.isFrameEdit) return;

    // On mobile, jump to the transactions pane so the results are visible.
    this.setState({ mobileTab: 'transactions' });

    this.fetchPaymentsData(
      null,
      false,
      false,
      { searchTerm: (this.state.paymentSearchTerm || '').trim() }
    );

    const items = this.state.missions.map((mission) => ({
      ...mission,
      clicked: false,
    }));

    if (this.state.showProjectTable) {
      this.closeProjectManager({
        missions: items,
        paymentDetail: null,
      });
      return;
    }
    this.setState({
      missions: items,
      paymentDetail: null,
    });
  }

  onProjectSettingClick(data) {
    // headerBackgroundPicture may be a comma-separated string (from the API)
    // or already an array (after an inline gallery quick-edit); normalize to
    // the array shape CreateGoal expects without assuming .split exists.
    data.headerBackgroundPicture = data.headerBackgroundPicture
      ? (Array.isArray(data.headerBackgroundPicture)
        ? data.headerBackgroundPicture
        : String(data.headerBackgroundPicture).split(","))
      : null;
    const passObj = {
      mission: data,
      isCreatingProject: false,
      buttonColor: this.state.organizationObj.buttonColor,
      username: this.state.username,
      subDomain: this.state.organizationObj.subDomain,
      currentPlan: this.state.organizationObj.currentPlan,
      // Drives the per-project payment method toggles (CP-280). The whole
      // object, not just paymentMethodCode: card, express and Link are gated
      // on other org fields, and OrgSettings is already passed it the same way.
      organizationObj: this.state.organizationObj,
      // Stripe readiness, so cards are only listed when donors can use them.
      charges_enabled: this.state.charges_enabled,
    };
    this.props.history.push({ pathname: "/CreateGoal", state: passObj });
  }

  onAddPaymentClick() {
    const { organization, organizationObj, missions, selectedProject } = this.state;

    const passObj = {
      organization,
      organizationObj,
      missions,
      selectedProject,
    };

    this.props.history.push({ pathname: "/AddOfflinePayments", state: passObj });
  }

  createProjectBtn() {
    let passObj = {
      isCreatingProject: true,
      buttonColor: this.state.organizationObj.buttonColor,
      username: this.state.username,
      currentPlan: this.state.organizationObj.currentPlan,
      // Drives the per-project payment method toggles (CP-280). The whole
      // object, not just paymentMethodCode: card, express and Link are gated
      // on other org fields, and OrgSettings is already passed it the same way.
      organizationObj: this.state.organizationObj,
      // Stripe readiness, so cards are only listed when donors can use them.
      charges_enabled: this.state.charges_enabled,
    };
    if (this.state.organizationObj.orderOfProjects)
      passObj.orderOfProjects = this.state.organizationObj.orderOfProjects;
    this.props.history.push({ pathname: "/CreateGoal", state: passObj });
  }

  orgSettingsBtn() {
    this.props.history.push({
      pathname: "/OrgSettings",
      state: {
        organizationObj: this.state.organizationObj,
        username: this.state.username,
      },
    });
  }

  toggleMenu() {
    this.setState(prevState => ({
      menuOpen: !prevState.menuOpen
    }));

    // Toggle body class for preventing scroll when menu is open
    if (!this.state.menuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
  }

  onLogoutClick() {
    this.props.orgLogout();
  }

  onScrollPaymentList() {
    const { current } = this.paymentListRef;

    if (!current) return;

    const { scrollTop, scrollHeight, clientHeight } = current;
  
    // Check if user has scrolled to bottom (with a small buffer)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      this.fetchMorePayments();
    }
  }

  fetchData() {
    if (this.state.fetching) return Promise.resolve();
    this.setState({ fetching: true });
    return simpleFetch("/projects", "GET", {
      subDomain: this.state.organizationObj.subDomain,
      lastId: this.state.lastId,
    }).then((res) => {
      if (res.body.length === 0) {
        this.setState({
          fetching: false,
          hasMore: false,
        });
        return;
      }
      const lastItem = res.body[res.body.length - 1];
      let missionData = res.body;
      for (var i = 0; i < missionData.length; i++) {
        missionData[i].clicked = false;
      }
      // Merge in cached donor/txn stats so newly-loaded pages carry them too.
      if (this.projectStatsMap) {
        missionData = missionData.map((m) =>
          this.mergeProjectStats(m, this.projectStatsMap)
        );
      }
      this.setState((prev) => ({
        fetching: false,
        missions: prev.missions.concat(missionData),
        lastId: !lastItem ? "" : lastItem.id,
        hasMore: true,
      }));
    });
  }

  loadAllProjects() {
    if (this.loadAllProjectsPromise) return this.loadAllProjectsPromise;
    if (!this.state.hasMore) return Promise.resolve();

    this.setState({ isLoadingAllProjects: true });

    const step = () => {
      if (!this.state.hasMore) return Promise.resolve();
      if (this.state.fetching) {
        // Another fetch (e.g. the initial mount load) is already in flight.
        // Wait for it to actually finish instead of spinning on the
        // already-resolved promise fetchData() returns while busy.
        return new Promise((resolve) => setTimeout(resolve, 200)).then(step);
      }
      return this.fetchData().then(() => step());
    };

    this.loadAllProjectsPromise = step().then(() => {
      this.loadAllProjectsPromise = null;
      this.setState({ isLoadingAllProjects: false });
      return this.loadProjectStats();
    });

    return this.loadAllProjectsPromise;
  }

  // Donor count / transaction count / first & last donation date per
  // project — fetched once the full project list has loaded (not baked
  // into GET /projects itself, since that endpoint has other lighter-weight
  // callers like ProjectSearchPicker that don't need this aggregation).
  // Average donation is derived here from Project.currentAmount rather than
  // returned by the endpoint, consistent with CP-222's export.
  // Merge one project's stats (from the projectStats map) onto a mission.
  mergeProjectStats(mission, statsMap) {
    const s = statsMap && statsMap[mission.id];
    const transactionCount = s ? s.transactionCount : 0;
    return {
      ...mission,
      donorCount: s ? s.donorCount : 0,
      transactionCount,
      averageDonation:
        transactionCount > 0 ? (mission.currentAmount || 0) / transactionCount : 0,
      firstDonation: s ? s.firstDonation : null,
      lastDonation: s ? s.lastDonation : null,
    };
  }

  loadProjectStats() {
    return simpleFetch("/organizations/projectStats", "GET", {})
      .then((res) => {
        if (res.status !== 200 || !res.body) return;
        const statsMap = res.body;
        // Cache so pages loaded later (pagination) also get stats merged.
        this.projectStatsMap = statsMap;
        this.setState((prev) => ({
          missions: prev.missions.map((m) => this.mergeProjectStats(m, statsMap)),
        }));
      })
      .catch(() => {});
  }

  toggleProjectTable() {
    if (this.state.showProjectTable) {
      this.closeProjectManager();
      return;
    }
    if (this.projectManagerCloseTimer) {
      clearTimeout(this.projectManagerCloseTimer);
      this.projectManagerCloseTimer = null;
    }
    this.setState(
      {
        showProjectTable: true,
        // Opens straight into Table view to match the "Table mode" button.
        projectManagerView: "table",
        projectManagerVisible: false,
      },
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.state.showProjectTable) {
              this.setState({ projectManagerVisible: true });
            }
          });
        });
      }
    );
    this.loadAllProjects();
  }

  openMediaGallery() {
    this.setState({ showMediaGallery: true });
  }

  closeMediaGallery() {
    this.setState({ showMediaGallery: false });
  }

  closeProjectManager(extraState = {}) {
    if (this.state.isFrameEdit) {
      this.saveProjectOrder();
      return;
    }
    if (!this.state.showProjectTable) {
      if (extraState && Object.keys(extraState).length) {
        this.setState(extraState);
      }
      return;
    }

    this.setState({
      ...extraState,
      projectManagerVisible: false,
    });

    if (this.projectManagerCloseTimer) {
      clearTimeout(this.projectManagerCloseTimer);
    }
    this.projectManagerCloseTimer = setTimeout(() => {
      this.projectManagerCloseTimer = null;
      this.setState({
        showProjectTable: false,
        projectManagerView: "cards",
        projectManagerVisible: false,
        isFrameEdit: false,
        missionsBeforeReorder: null,
        dragProjectId: null,
        dropProjectIndex: null,
        isSavingProjectOrder: false,
      });
    }, 280);
  }

  setProjectManagerView(view) {
    if (this.state.isFrameEdit) return;
    this.setState({ projectManagerView: view });
    if (view === "table" || view === "cards") {
      this.loadAllProjects();
    }
  }

  leftArrowBtn(arrNo) {
    const arr = this.state.missions;
    if (arrNo - 1 < 0) return;
    this.setState({
      missions: arrayMove(arr, arrNo, arrNo - 1),
    });
  }

  rightArrowBtn(arrNo) {
    const arr = this.state.missions;
    if (arrNo + 1 >= arr.length) return;
    this.setState({
      missions: arrayMove(arr, arrNo, arrNo + 1),
    });
  }

  handleProjectDragStart(id, event) {
    this.setState({ dragProjectId: id });
    try {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(id));
    } catch (err) {
      // Safari can throw on setData in some cases
    }
  }

  handleProjectDragOver(index, event) {
    if (this.state.dragProjectId == null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const isUpper = event.clientY < rect.top + rect.height / 2;
    const nextDropIndex = isUpper ? index : index + 1;
    if (this.state.dropProjectIndex !== nextDropIndex) {
      this.setState({ dropProjectIndex: nextDropIndex });
    }
  }

  handleProjectDragLeaveList(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.setState({ dropProjectIndex: null });
    }
  }

  handleProjectDrop(event) {
    event.preventDefault();
    const { dragProjectId, dropProjectIndex, missions } = this.state;
    if (dragProjectId == null || dropProjectIndex == null) {
      this.setState({ dragProjectId: null, dropProjectIndex: null });
      return;
    }

    const fromIndex = missions.findIndex(
      (m) => String(m.id) === String(dragProjectId)
    );
    if (fromIndex < 0) {
      this.setState({ dragProjectId: null, dropProjectIndex: null });
      return;
    }

    let toIndex = dropProjectIndex;
    if (fromIndex < toIndex) toIndex -= 1;
    if (toIndex === fromIndex) {
      this.setState({ dragProjectId: null, dropProjectIndex: null });
      return;
    }

    this.setState({
      missions: arrayMove(missions, fromIndex, toIndex),
      dragProjectId: null,
      dropProjectIndex: null,
    });
  }

  handleProjectDragEnd() {
    this.setState({ dragProjectId: null, dropProjectIndex: null });
  }

  saveProjectOrder() {
    if (this.state.isSavingProjectOrder || this.savingProjectOrder) {
      return this.savingProjectOrder || Promise.resolve(false);
    }

    let arr = [];
    let orderOfProjects = [];
    if (this.state.organizationObj.orderOfProjects) {
      orderOfProjects = orderOfProjects.concat(
        this.state.organizationObj.orderOfProjects
      );
    }
    const missionsArr = this.state.missions;

    for (var i = 0; i < missionsArr.length; i++) {
      const id = missionsArr[i].id.toString();
      if (!id) continue;
      arr.push(id);
      const index = orderOfProjects.indexOf(id);
      if (index > -1) {
        orderOfProjects.splice(index, 1);
      }
    }
    const neworder = arr.concat(orderOfProjects);

    this.setState({ isSavingProjectOrder: true });

    this.savingProjectOrder = simpleFetch("/organizations/updateorders", "PUT", {
      orderOfProjects: neworder,
    })
      .then((res) => {
        this.savingProjectOrder = null;
        if (res && (res.status === 200 || res.status === 204)) {
          this.setState((prev) => ({
            isFrameEdit: false,
            isSavingProjectOrder: false,
            dragProjectId: null,
            dropProjectIndex: null,
            missionsBeforeReorder: null,
            organizationObj: {
              ...prev.organizationObj,
              orderOfProjects: neworder,
            },
          }));
          return true;
        }
        this.setState({ isSavingProjectOrder: false });
        return false;
      })
      .catch(() => {
        this.savingProjectOrder = null;
        this.setState({ isSavingProjectOrder: false });
        return false;
      });

    return this.savingProjectOrder;
  }

  cancelProjectReorder() {
    if (this.state.isSavingProjectOrder) return;
    const restored = this.state.missionsBeforeReorder;
    this.setState({
      isFrameEdit: false,
      dragProjectId: null,
      dropProjectIndex: null,
      missionsBeforeReorder: null,
      missions: restored ? restored.slice() : this.state.missions,
    });
  }

  handleProjectReorderToggle() {
    if (this.state.isFrameEdit) {
      this.saveProjectOrder();
      return;
    }

    // Reorder happens in place, in whichever view is already open — the desktop
    // sidebar compact list or the inline manager — never by expanding into the
    // full takeover. showProjectTable is left untouched.
    const enterReorder = () => {
      this.setState((prev) => ({
        isFrameEdit: true,
        projectManagerView: "cards",
        projectSearchTerm: "",
        projectStatusFilter: "",
        dragProjectId: null,
        dropProjectIndex: null,
        missionsBeforeReorder: prev.missions.slice(),
      }));
    };

    this.loadAllProjects().then(enterReorder);
  }

  handleProjectStatusChange(mission, status) {
    return this.handleProjectFieldSave(mission, { status });
  }

  handleProjectFieldSave(mission, fields) {
    return simpleFetch("/projects/" + mission.id, "PUT", fields).then(
      (res) => {
        if (res && res.status === 200) {
          this.setState({
            missions: this.state.missions.map((m) =>
              m.id === mission.id ? { ...m, ...fields } : m
            ),
          });
          return true;
        }
        return false;
      }
    );
  }

  handleBulkUpdate(projectIds, fields) {
    const ops = [];
    const idSet = new Set(projectIds);

    if (fields.status !== undefined) {
      ops.push(
        simpleFetch("/projects/bulk-status", "PUT", {
          projectIds,
          status: fields.status,
        }).then((res) => {
          if (res && res.status === 200) {
            this.setState((prev) => ({
              missions: prev.missions.map((m) =>
                idSet.has(m.id) ? { ...m, status: fields.status } : m
              ),
            }));
            return true;
          }
          return false;
        })
      );
    }

    const patch = {};
    if (fields.donation !== undefined) patch.donation = fields.donation;
    if (fields.targetAmount !== undefined) {
      patch.targetAmount = fields.targetAmount;
    }
    if (Object.keys(patch).length) {
      // Chunk per-id PUTs — a 1,000-row selection must not fire 1,000
      // concurrent requests.
      const CHUNK_SIZE = 25;
      const putChunked = (ids) => {
        if (!ids.length) return Promise.resolve([]);
        const chunk = ids.slice(0, CHUNK_SIZE);
        return Promise.all(
          chunk.map((id) =>
            simpleFetch("/projects/" + id, "PUT", patch).then((res) => ({
              id,
              ok: !!(res && res.status === 200),
            }))
          )
        ).then((results) =>
          putChunked(ids.slice(CHUNK_SIZE)).then((rest) =>
            results.concat(rest)
          )
        );
      };
      ops.push(
        putChunked(projectIds).then((results) => {
          const okIds = new Set(
            results.filter((r) => r.ok).map((r) => r.id)
          );
          if (okIds.size) {
            this.setState((prev) => ({
              missions: prev.missions.map((m) =>
                okIds.has(m.id) ? { ...m, ...patch } : m
              ),
            }));
          }
          return results.length > 0 && results.every((r) => r.ok);
        })
      );
    }

    if (!ops.length) return Promise.resolve(false);
    return Promise.all(ops).then((results) => results.every(Boolean));
  }

  handleProjectSearchChange(event) {
    const value = event.target.value;
    this.setState({ projectSearchTerm: value });
    if (this.projectSearchDebounce) {
      clearTimeout(this.projectSearchDebounce);
    }
    if (value.trim()) {
      this.projectSearchDebounce = setTimeout(() => {
        this.projectSearchDebounce = null;
        this.loadAllProjects();
      }, 300);
    } else {
      this.projectSearchDebounce = null;
    }
  }

  handleProjectStatusFilterChange(event) {
    const value = event.target.value;
    this.setState({ projectStatusFilter: value });
    if (value) {
      this.loadAllProjects();
    }
  }

  getFilteredMissions() {
    const term = (this.state.projectSearchTerm || "").trim().toLowerCase();
    const status = this.state.projectStatusFilter || "";
    if (!term && !status) return this.state.missions;

    return this.state.missions.filter((mission) => {
      if (status && mission.status !== status) return false;
      if (term) {
        const title = getProjectTitle(this.props.language, mission) || "";
        if (!title.toLowerCase().includes(term)) return false;
      }
      return true;
    });
  }

  fetchPaymentsData(
    projectData = null, // set projectData to null to get all payments
    ignoreLoading = false,
    preservePaymentDetail = false,
    options = {}
  ) {
    if (!ignoreLoading && this.state.selectedProject.loadingList) return false;

    const hasSearchOverride = Object.prototype.hasOwnProperty.call(options, 'searchTerm');
    const hasStatusOverride = Object.prototype.hasOwnProperty.call(options, 'status');
    const hasTransactionTypeOverride = Object.prototype.hasOwnProperty.call(options, 'transactionType');
    const providedSearchTerm = hasSearchOverride ? options.searchTerm : undefined;
    const normalizedSearchTerm = hasSearchOverride
      ? (providedSearchTerm || '').trim()
      : (this.state.selectedProject.searchTerm || '');
    const statusFilter = hasStatusOverride
      ? (options.status || '')
      : (this.state.paymentStatusFilter || '');
    const transactionTypeFilter = hasTransactionTypeOverride
      ? (options.transactionType || '')
      : (this.state.paymentTransactionTypeFilter || '');

    const previousPaymentDetail = preservePaymentDetail ? this.state.paymentDetail : null;

    let missions = this.state.missions;
    if (projectData && !preservePaymentDetail) {
      missions = missions.map((mission) => ({
        ...mission,
        clicked: mission.id === projectData.id,
      }));
    }

    const nextSelectedProject = {
      ...this.state.selectedProject,
      loadingList: true,
      hasMore: true,
      isFetchingMore: false,
      projectId: projectData ? projectData.id : undefined,
    };

    if (hasSearchOverride) {
      nextSelectedProject.searchTerm = normalizedSearchTerm;
    } else if (typeof nextSelectedProject.searchTerm !== 'string') {
      nextSelectedProject.searchTerm = '';
    }

    if (!preservePaymentDetail || projectData || hasSearchOverride) {
      nextSelectedProject.lastId = 0;
      nextSelectedProject.lastUpdatedAt = '';
    }

    this.setState({
      selectedProject: nextSelectedProject,
      missions,
    });

    const sortField = this.state.paymentSortField || ORG_PAYMENT_LIST_SORT;
    const sortOrder = this.state.paymentSortOrder || 'desc';

    const reqBody = {
      limit: ORG_PAYMENT_LIST_LIMIT,
      sort: sortField,
      sortOrder,
    };

    if (nextSelectedProject.projectId) {
      reqBody.projectId = nextSelectedProject.projectId;
    }
    if (nextSelectedProject.searchTerm) {
      reqBody.searchTerm = nextSelectedProject.searchTerm;
    }
    if (this.state.paymentModeFilter) reqBody.paymentMode = this.state.paymentModeFilter;
    if (statusFilter) {
      reqBody.status = statusFilter;
    }
    if (transactionTypeFilter) {
      reqBody.transactionType = transactionTypeFilter;
    }
    const dateRangeStart = this.state.paymentDateRangeStart;
    const dateRangeEnd = this.state.paymentDateRangeEnd;
    if (dateRangeStart && dateRangeEnd) {
      const startNormalized = new Date(dateRangeStart);
      startNormalized.setHours(0, 0, 0, 0);
      const endNormalized = new Date(dateRangeEnd);
      endNormalized.setHours(23, 59, 59, 999);
      reqBody.startDate = startNormalized.toISOString();
      reqBody.endDate = endNormalized.toISOString();
      reqBody.dateField = this.state.paymentDateField || 'scheduledDate';
    }

    const requestToken = ++this.paymentsRequestSeq;
    this.latestPaymentsRequestSeq = requestToken;

    simpleFetch('/organizations/payments', 'GET', reqBody)
      .then((res) => {
        if (requestToken !== this.latestPaymentsRequestSeq) return;

        const isSuccess = res.status === 200 && Array.isArray(res.body);
        const payload = isSuccess ? res.body : [];
        const lastItem = payload.length ? payload[payload.length - 1] : null;

        const mappedPayments = payload.map((payment) => ({
          ...payment,
          clicked: false,
        }));

        let nextPaymentDetail = previousPaymentDetail;
        if (previousPaymentDetail) {
          const preserved = mappedPayments.find((payment) => payment.id === previousPaymentDetail.id);
          if (preserved) {
            preserved.clicked = true;
            nextPaymentDetail = {
              ...preserved,
              clicked: true,
            };
          } else {
            nextPaymentDetail = null;
          }
        } else if (!preservePaymentDetail) {
          nextPaymentDetail = null;
        }

        const linkedPaymentId = !this.receiptLinkOpened ? receiptPaymentId(this.props.location?.search) : null;
        if (linkedPaymentId) {
          nextPaymentDetail = mappedPayments.find(payment => payment.id === linkedPaymentId) || { id: linkedPaymentId };
          this.receiptLinkOpened = true;
        }

        this.setState((prevState) => {
          if (requestToken !== this.latestPaymentsRequestSeq) {
            return null;
          }

          const updatedSelectedProject = {
            ...prevState.selectedProject,
            loadingList: false,
            paymentsList: mappedPayments,
            lastId: lastItem ? lastItem.id : 0,
            lastUpdatedAt: lastItem ? lastItem.updatedAt : '',
            lastSortValue: lastItem ? (lastItem[sortField] || lastItem.updatedAt) : '',
            hasMore: payload.length === ORG_PAYMENT_LIST_LIMIT,
            searchTerm: nextSelectedProject.searchTerm || '',
          };

          const nextState = {
            selectedProject: updatedSelectedProject,
          };

          if (nextPaymentDetail) {
            nextState.paymentDetail = nextPaymentDetail;
          } else if (!preservePaymentDetail) {
            nextState.paymentDetail = null;
          }

          return nextState;
        }, () => {
          if (!preservePaymentDetail && this.paymentListRef.current) {
            this.paymentListRef.current.scrollTop = 0;
          }
        });
      })
      .catch(() => {
        if (requestToken !== this.latestPaymentsRequestSeq) return;
        this.setState((prevState) => ({
          selectedProject: {
            ...prevState.selectedProject,
            loadingList: false,
            isFetchingMore: false,
          },
        }));
      });
  }

  // Dashboard helpers
  setDashboardPeriod(period) {
    this.setState({ dashboardPeriod: period });
  }

  setRightPaneView(view) {
    // view: 'list' | 'dashboard'
    this.setState({ showDashboard: view === 'dashboard' });
  }

  // Mobile (<=750px) tab switch: 'projects' (inline manager cards),
  // 'list' (inline manager table — where Manage Images lives), or
  // 'transactions'. No effect on desktop.
  setMobileTab(tab) {
    if (tab === 'projects' || tab === 'list') {
      // Both show the inline manager; the tab picks its view. Needs the full
      // project list (+ stats), not just the first page.
      this.setState({
        mobileTab: tab,
        projectManagerView: tab === 'list' ? 'table' : 'cards',
      });
      this.loadAllProjects();
    } else {
      this.setState({ mobileTab: tab });
    }
  }

  toggleSeriesVisibility(key) {
    const next = { ...this.state.visibleSeries, [key]: !this.state.visibleSeries[key] };
    // Ensure at least one is visible
    if (!next.all && !next.payment && !next.donation) {
      return; // ignore if would turn all off
    }
    this.setState({ visibleSeries: next });
  }

  handleChartMouseMove(event, W, H, leftPad, rightPad, topPad, bottomPad, count) {
    if (!this.volumeSvgRef.current || !count) return;
    const rect = this.volumeSvgRef.current.getBoundingClientRect();
    // Map screen x to viewBox x with preserveAspectRatio="xMidYMid meet"
    const scale = Math.min(rect.width / W, rect.height / H);
    const drawWidth = W * scale;
    // const drawHeight = H * scale;
    const offsetX = (rect.width - drawWidth) / 2;
    // const offsetY = (rect.height - drawHeight) / 2; // not needed for x mapping

    let xPxRaw = event.clientX - rect.left - offsetX;
    xPxRaw = Math.max(0, Math.min(drawWidth, xPxRaw));
    const xVbRaw = xPxRaw / scale;
    const xVb = Math.max(leftPad, Math.min(W - rightPad, xVbRaw));
    const spanVb = Math.max(1, W - leftPad - rightPad);
    const ratio = (xVb - leftPad) / spanVb;
    const idx = Math.max(0, Math.min(count - 1, Math.round(ratio * (count - 1))));
    this.setState({ chartHoverIndex: idx, chartHoverLeft: offsetX + xPxRaw });
  }

  handleChartMouseLeave() {
    this.setState({ chartHoverIndex: null });
  }

  handlePaymentSearchChange(event) {
    const value = event.target.value;
    this.setState({ paymentSearchTerm: value }, () => {
      if (this.paymentSearchDebounce) {
        clearTimeout(this.paymentSearchDebounce);
      }
      this.paymentSearchDebounce = setTimeout(() => {
        this.applyPaymentSearch();
      }, 300);
    });
  }

  applyPaymentSearch() {
    this.paymentSearchDebounce = null;
    const rawTerm = this.state.paymentSearchTerm || '';
    const trimmed = rawTerm.trim();
    const currentSearch = this.state.selectedProject.searchTerm || '';

    if (trimmed === currentSearch) {
      return;
    }

    const activeProjectId = this.state.selectedProject.projectId;
    const projectData = activeProjectId ? { id: activeProjectId } : null;
    const status = this.state.paymentStatusFilter || '';
    const transactionType = this.state.paymentTransactionTypeFilter || '';

    this.fetchPaymentsData(projectData, true, false, { searchTerm: trimmed, status, transactionType });
  }

  handlePaymentModeFilterChange(event) {
    this.setState({ paymentModeFilter: event.target.value }, () => this.refetchPaymentsWithCurrentFilters());
  }

  handlePaymentStatusFilterChange(event) {
    const status = event.target.value;
    this.setState({ paymentStatusFilter: status }, () => {
      const activeProjectId = this.state.selectedProject.projectId;
      const projectData = activeProjectId ? { id: activeProjectId } : null;
      const searchTerm = (this.state.paymentSearchTerm || '').trim();
      const transactionType = this.state.paymentTransactionTypeFilter || '';

      this.fetchPaymentsData(projectData, true, false, { searchTerm, status, transactionType });
    });
  }

  handlePaymentTransactionTypeFilterChange(event) {
    const transactionType = event.target.value;
    this.setState({ paymentTransactionTypeFilter: transactionType }, () => {
      const activeProjectId = this.state.selectedProject.projectId;
      const projectData = activeProjectId ? { id: activeProjectId } : null;
      const searchTerm = (this.state.paymentSearchTerm || '').trim();
      const status = this.state.paymentStatusFilter || '';

      this.fetchPaymentsData(projectData, true, false, { searchTerm, status, transactionType });
    });
  }

  handlePaymentSortChange(event) {
    const sortField = event.target.value;
    const nextState = { paymentSortField: sortField };
    // Receipt numbers are sequenced per transaction type, so that sort requires
    // a single type. Every other sort keeps whatever type the user picked.
    if (sortField === 'organizationReceiptId') {
      nextState.paymentSortOrder = 'desc';
      if (!this.state.paymentTransactionTypeFilter) {
        nextState.paymentTransactionTypeFilter = 'donation';
      }
    }

    this.setState(nextState, () => {
      const activeProjectId = this.state.selectedProject.projectId;
      const projectData = activeProjectId ? { id: activeProjectId } : null;
      const searchTerm = (this.state.paymentSearchTerm || '').trim();
      const status = this.state.paymentStatusFilter || '';
      const transactionType = this.state.paymentTransactionTypeFilter || '';

      this.fetchPaymentsData(projectData, true, false, { searchTerm, status, transactionType });
    });
  }

  handlePaymentSortOrderChange(event) {
    const sortOrder = event.target.value;

    this.setState({ paymentSortOrder: sortOrder }, () => {
      const activeProjectId = this.state.selectedProject.projectId;
      const projectData = activeProjectId ? { id: activeProjectId } : null;
      const searchTerm = (this.state.paymentSearchTerm || '').trim();
      const status = this.state.paymentStatusFilter || '';
      const transactionType = this.state.paymentTransactionTypeFilter || '';

      this.fetchPaymentsData(projectData, true, false, { searchTerm, status, transactionType });
    });
  }

  computeDateRangeForPreset(preset) {
    if (preset !== '7d' && preset !== '30d') return { start: null, end: null };
    const days = preset === '7d' ? 7 : 30;
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }

  refetchPaymentsWithCurrentFilters() {
    const activeProjectId = this.state.selectedProject.projectId;
    const projectData = activeProjectId ? { id: activeProjectId } : null;
    const searchTerm = (this.state.paymentSearchTerm || '').trim();
    const status = this.state.paymentStatusFilter || '';
    const transactionType = this.state.paymentTransactionTypeFilter || '';
    this.fetchPaymentsData(projectData, true, false, { searchTerm, status, transactionType });
  }

  handlePaymentDateFieldChange(field) {
    this.setState({ paymentDateField: field }, () => {
      if (this.state.paymentDateRangeStart && this.state.paymentDateRangeEnd) {
        this.refetchPaymentsWithCurrentFilters();
      }
    });
  }

  handlePaymentDateRangePresetChange(preset) {
    let nextStart = null;
    let nextEnd = null;
    if (preset === '7d' || preset === '30d') {
      const range = this.computeDateRangeForPreset(preset);
      nextStart = range.start;
      nextEnd = range.end;
    } else if (preset === 'custom') {
      if (this.state.paymentDateRangeStart && this.state.paymentDateRangeEnd) {
        nextStart = this.state.paymentDateRangeStart;
        nextEnd = this.state.paymentDateRangeEnd;
      } else {
        const range = this.computeDateRangeForPreset('30d');
        nextStart = range.start;
        nextEnd = range.end;
      }
    }
    this.setState({
      paymentDateRangePreset: preset,
      paymentDateRangeStart: nextStart,
      paymentDateRangeEnd: nextEnd,
    }, () => {
      this.refetchPaymentsWithCurrentFilters();
    });
  }

  handlePaymentDateRangeChange(update) {
    const [start, end] = Array.isArray(update) ? update : [null, null];
    this.setState({
      paymentDateRangePreset: 'custom',
      paymentDateRangeStart: start,
      paymentDateRangeEnd: end,
    }, () => {
      if (start && end) {
        this.refetchPaymentsWithCurrentFilters();
      }
    });
  }

  toggleFilterPanel() {
    this.setState((prev) => ({ filterPanelExpanded: !prev.filterPanelExpanded }));
  }

  clearAllFilters() {
    const isReceiptIdSort = this.state.paymentSortField === 'organizationReceiptId';
    const nextTransactionType = isReceiptIdSort
      ? (this.state.paymentTransactionTypeFilter || 'donation')
      : '';
    this.setState({
      paymentStatusFilter: '',
      paymentModeFilter: '',
      paymentTransactionTypeFilter: nextTransactionType,
      paymentDateRangePreset: '',
      paymentDateRangeStart: null,
      paymentDateRangeEnd: null,
    }, () => {
      const activeProjectId = this.state.selectedProject.projectId;
      const projectData = activeProjectId ? { id: activeProjectId } : null;
      const searchTerm = (this.state.paymentSearchTerm || '').trim();

      this.fetchPaymentsData(projectData, true, false, {
        searchTerm,
        status: '',
        transactionType: nextTransactionType,
      });
    });
  }

  filterByPeriod(dateString) {
    const { dashboardPeriod } = this.state;
    if (dashboardPeriod === 'all') return true;
    const now = new Date();
    const updatedAt = new Date(dateString);
    const ONE_DAY = 24 * 60 * 60 * 1000;
    const rangeDays = dashboardPeriod === '7d' ? 7 : 30;
    return (now - updatedAt) <= rangeDays * ONE_DAY;
  }

  computeStatsFromPayments(payments) {
    if (!Array.isArray(payments) || payments.length === 0) {
      return {
        totalAmount: 0,
        totalCount: 0,
        avgAmount: 0,
        successRate: 0,
        donation: { amount: 0, count: 0 },
        payment: { amount: 0, count: 0 },
        topProjects: [],
      };
    }

    const filtered = payments.filter(p => this.filterByPeriod(p.updatedAt));
    let totalAmount = 0;
    let totalCount = 0;
    let successCount = 0;
    let donationAmount = 0;
    let donationCount = 0;
    let paymentAmount = 0;
    let paymentCount = 0;
    const projectTotals = {};

    filtered.forEach(p => {
      const amount = Number(p.amount) || 0;
      totalAmount += amount;
      totalCount += 1;
      if (p.status === 'complete') successCount += 1;

      const isDonation = (p.type === 'donation') || (p.projectName === 'General Donation');
      if (isDonation) {
        donationAmount += amount;
        donationCount += 1;
      } else {
        paymentAmount += amount;
        paymentCount += 1;
      }

      const projectKey = p.projectId || p.projectName || 'General';
      if (!projectTotals[projectKey]) {
        projectTotals[projectKey] = { amount: 0, count: 0, title: null };
      }
      projectTotals[projectKey].amount += amount;
      projectTotals[projectKey].count += 1;
      // Label the bar exactly as the payment list labels its rows, so the same
      // project cannot read one way here and another way there. The key is a
      // bare project id, so without a name the widget shows a number when the
      // project is outside the loaded page of the project list (CP-274).
      if (!projectTotals[projectKey].title) {
        const label = getPaymentProjectLabel(
          this.props.language,
          p,
          this.state.missions
        );
        if (label) projectTotals[projectKey].title = label;
      }
    });

    const avgAmount = totalCount ? totalAmount / totalCount : 0;
    const successRate = totalCount ? (successCount / totalCount) * 100 : 0;
    const donationShare = totalAmount ? (donationAmount / totalAmount) * 100 : 0;
    const paymentShare = totalAmount ? (paymentAmount / totalAmount) * 100 : 0;

    const latestPayment = filtered
      .slice()
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0] || null;

    // Resolve project titles from missions when possible
    const topProjects = Object.keys(projectTotals)
      .map(key => {
        let title = 'General';
        if (key === 0 || key === 'General' || key === 'General Donation' || key === 'General Payment') {
          title = typeof key === 'string' ? key : 'General';
        } else {
          // Already resolved above, by the same rules as the payment list.
          title = projectTotals[key].title
            || (typeof key === 'string' ? key : `Project ${key}`);
        }
        return { key, title, amount: projectTotals[key].amount, count: projectTotals[key].count };
      })
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);

    return {
      totalAmount,
      totalCount,
      avgAmount,
      successRate,
      donation: { amount: donationAmount, count: donationCount },
      payment: { amount: paymentAmount, count: paymentCount },
      donationShare,
      paymentShare,
      latestPayment,
      topProjects,
    };
  }

  computeVolumeSeries(payments) {
    const { dashboardPeriod } = this.state;
    if (!Array.isArray(payments) || payments.length === 0) {
      return { series: [], max: 0 };
    }

    // Filter by selected period; for 'all' we keep all
    let filtered = payments.slice();
    if (dashboardPeriod !== 'all') {
      filtered = payments.filter(function(p) { return (p && p.updatedAt) ? (this.filterByPeriod(p.updatedAt)) : false; }.bind(this));
    }

    // Consider only successful transactions for volume visualization
    filtered = filtered.filter(function(p) { return p.status === 'complete'; });

    // Build date buckets
    const now = new Date();
    let daysSpan = 30;
    if (dashboardPeriod === '7d') daysSpan = 7;
    if (dashboardPeriod === '30d') daysSpan = 30;

    // For 'all', span across min..max but cap at 90 days for readability
    if (dashboardPeriod === 'all' && filtered.length) {
      const times = filtered.map(function(p) { return new Date(p.updatedAt).getTime(); });
      const minTime = Math.min.apply(null, times);
      const maxTime = Math.max.apply(null, times);
      const spanDays = Math.max(1, Math.ceil((maxTime - minTime) / (24 * 60 * 60 * 1000)) + 1);
      daysSpan = Math.min(90, spanDays);
    }

    // Prepare bucket map for the last N days (oldest -> newest)
    const buckets = [];
    for (var i = daysSpan - 1; i >= 0; i--) {
      var d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      var key = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
      var label = (d.getMonth() + 1) + '/' + d.getDate();
      buckets.push({ key: key, label: label, all: 0, payment: 0, donation: 0 });
    }

    function keyFromDate(date) {
      var d = new Date(date);
      return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
    }

    var mapIndex = {};
    buckets.forEach(function(b, idx) { mapIndex[b.key] = idx; });

    filtered.forEach(function(p) {
      var key = keyFromDate(p.updatedAt);
      if (mapIndex.hasOwnProperty(key)) {
        var idx = mapIndex[key];
        var amt = Number(p.amount) || 0;
        buckets[idx].all += amt;
        var isDonation = (p.type === 'donation') || (p.projectName === 'General Donation');
        if (isDonation) {
          buckets[idx].donation += amt;
        } else {
          buckets[idx].payment += amt;
        }
      }
    });

    var max = 0;
    buckets.forEach(function(b) {
      max = Math.max(max, b.all, b.payment, b.donation);
    });

    return { series: buckets, max: max };
  }

  renderStatsDashboard(buttonStyle, textObj) {
    const payments = this.state.selectedProject.paymentsList || [];
    const stats = this.computeStatsFromPayments(payments);
    const volume = this.computeVolumeSeries(payments);

    const periodChips = [
      { key: '7d', label: '7D' },
      { key: '30d', label: '30D' },
      { key: 'all', label: (textObj && textObj.all) || 'All' },
    ];
    const hasPayments = Array.isArray(payments) && payments.length > 0;
    const hasVolume = Array.isArray(volume.series) && volume.series.length > 0;
    const transactionLabel = stats.totalCount === 1
      ? ((textObj && textObj.transaction) || 'transaction')
      : ((textObj && textObj.transactions) || 'transactions');
    const successLabel = (textObj && textObj.successRate) || 'success rate';
    const donationLabel = (textObj && textObj.donations) || 'Donations';
    const paymentLabel = (textObj && textObj.payments) || 'Payments';
    const formatCurrency = (value, decimalScale = 2) => (
      <NumericFormat
        value={value}
        displayType="text"
        valueIsNumericString={true}
        thousandSeparator={true}
        decimalScale={decimalScale}
        fixedDecimalScale={decimalScale !== null}
      />
    );

    const latestPaymentDate = stats.latestPayment ? new Date(stats.latestPayment.updatedAt) : null;
    const latestPaymentAmount = stats.latestPayment ? Number(stats.latestPayment.amount) || 0 : 0;
    const latestPaymentProject = stats.latestPayment ? (stats.latestPayment.projectName || stats.latestPayment.projectTitle || stats.latestPayment.projectId || '—') : '—';

    const peakVolume = hasVolume
      ? volume.series.reduce((max, entry) => (entry.all > max.all ? entry : max), volume.series[0])
      : null;

    /* eslint-disable react/prop-types */
    const Tile = ({ title, value, sub, tone }) => (
      <article className={`AccountOverview-statTile${tone ? ' tone-' + tone : ''}`} role="listitem">
        <div className="AccountOverview-statTitle">{title}</div>
        <div className="AccountOverview-statValue">{value}</div>
        {sub ? <div className="AccountOverview-statSub">{sub}</div> : null}
      </article>
    );
    /* eslint-enable react/prop-types */

    return (
      <section
        className="AccountOverview-statsDashboard"
        aria-labelledby="AccountOverviewStatsHeading"
      >
        <header className="AccountOverview-statsHeader">
          <div className="AccountOverview-statsHeading">
            <h2 id="AccountOverviewStatsHeading" className="AccountOverview-statsTitle">{(textObj && textObj.overviewHeading) || 'Overview'}</h2>
            <p className="AccountOverview-statsSubtitle">
              {hasPayments
                ? `${stats.totalCount} ${transactionLabel} · ${Math.round(stats.successRate)}% ${successLabel}`
                : (textObj && textObj.noTransactionsYet) || 'No transactions yet for the selected period'}
            </p>
          </div>
          <div className="AccountOverview-periodChips" role="group" aria-label={(textObj && textObj.periodLabel) || 'Period filter'}>
            {periodChips.map(chip => {
              const isActive = this.state.dashboardPeriod === chip.key;
              const appliedStyle = isActive ? buttonStyle : {};
              return (
                <button
                  type="button"
                  key={chip.key}
                  className={`AccountOverview-chip ${isActive ? 'active' : ''}`}
                  aria-pressed={isActive}
                  onClick={() => this.setDashboardPeriod(chip.key)}
                  style={appliedStyle}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </header>

        <div className="AccountOverview-statsGrid" role="list">
          <Tile
            title={(textObj && textObj.totalVolume) || 'Total volume'}
            value={<span>HK$ {formatCurrency(stats.totalAmount)}</span>}
            sub={`${stats.totalCount} ${transactionLabel}`}
            tone="primary"
          />
          <Tile
            title={(textObj && textObj.avgAmount) || 'Average amount'}
            value={<span>HK$ {formatCurrency(stats.avgAmount)}</span>}
            sub={`${Math.round(stats.successRate)}% ${successLabel}`}
          />
          <Tile
            title={donationLabel}
            value={<span>HK$ {formatCurrency(stats.donation.amount)}</span>}
            sub={`${stats.donation.count} ${((textObj && textObj.count) || 'count')} · ${Math.round(stats.donationShare)}%`}
            tone="neutral"
          />
          <Tile
            title={paymentLabel}
            value={<span>HK$ {formatCurrency(stats.payment.amount)}</span>}
            sub={`${stats.payment.count} ${((textObj && textObj.count) || 'count')} · ${Math.round(stats.paymentShare)}%`}
            tone="neutral"
          />
        </div>

        <div className="AccountOverview-inlineStats" role="list">
          <div className="AccountOverview-inlineCard" role="listitem">
            <div className="AccountOverview-inlineLabel">{(textObj && textObj.lastPayment) || 'Last payment'}</div>
            <div className="AccountOverview-inlineValue">
              {latestPaymentDate ? latestPaymentDate.toLocaleString() : ((textObj && textObj.noRecentActivity) || 'No recent activity')}
            </div>
            {latestPaymentDate ? (
              <div className="AccountOverview-inlineSub">
                HK$ {formatCurrency(latestPaymentAmount)} · {latestPaymentProject}
              </div>
            ) : null}
          </div>
          <div className="AccountOverview-inlineCard" role="listitem">
            <div className="AccountOverview-inlineLabel">{(textObj && textObj.peakDay) || 'Peak day'}</div>
            <div className="AccountOverview-inlineValue">
              {peakVolume ? peakVolume.label : ((textObj && textObj.noData) || 'No data')}
            </div>
            {peakVolume ? (
              <div className="AccountOverview-inlineSub">
                HK$ {formatCurrency(peakVolume.all)} {(textObj && textObj.allTransactions) || 'All transactions'}
              </div>
            ) : null}
          </div>
        </div>

        <div className="AccountOverview-breakdown">
          <div className="AccountOverview-sectionTitle">{(textObj && textObj.breakdown) || 'Payment breakdown'}</div>
          <div className="AccountOverview-breakdownGrid">
            <div className="AccountOverview-breakdownTile">
              <div className="AccountOverview-breakdownHeader">
                <span className="AccountOverview-breakdownLabel">{donationLabel}</span>
                <span className="AccountOverview-breakdownValue">HK$ {formatCurrency(stats.donation.amount)}</span>
              </div>
              <div className="AccountOverview-breakdownMeter">
                <div className="AccountOverview-breakdownFill donations" style={{ width: `${Math.min(100, Math.round(stats.donationShare))}%` }} />
              </div>
              <div className="AccountOverview-breakdownSub">
                {stats.donation.count} {((textObj && textObj.count) || 'count')} · {Math.round(stats.donationShare)}%
              </div>
            </div>
            <div className="AccountOverview-breakdownTile">
              <div className="AccountOverview-breakdownHeader">
                <span className="AccountOverview-breakdownLabel">{paymentLabel}</span>
                <span className="AccountOverview-breakdownValue">HK$ {formatCurrency(stats.payment.amount)}</span>
              </div>
              <div className="AccountOverview-breakdownMeter">
                <div className="AccountOverview-breakdownFill payments" style={{ width: `${Math.min(100, Math.round(stats.paymentShare))}%` }} />
              </div>
              <div className="AccountOverview-breakdownSub">
                {stats.payment.count} {((textObj && textObj.count) || 'count')} · {Math.round(stats.paymentShare)}%
              </div>
            </div>
          </div>
        </div>

        {stats.topProjects.length ? (
          <div className="AccountOverview-topProjects">
            <div className="AccountOverview-sectionTitle">{(textObj && textObj.topProjects) || 'Top projects'}</div>
            <div className="AccountOverview-projectBars">
              {stats.topProjects.map((p, idx) => (
                <div key={p.key} className="AccountOverview-projectBar">
                  <div className="AccountOverview-projectMeta">
                    <div className="AccountOverview-projectName" title={p.title}>
                      <span className="AccountOverview-projectRank">#{idx + 1}</span>
                      {p.title}
                    </div>
                    <div className="AccountOverview-projectAmount">HK$ {formatCurrency(p.amount)}</div>
                  </div>
                  <div className="AccountOverview-barTrack">
                    <div
                      className="AccountOverview-barFill"
                      style={{ width: `${stats.topProjects[0].amount ? Math.round((p.amount / stats.topProjects[0].amount) * 100) : 0}%` }}
                    />
                  </div>
                  <div className="AccountOverview-projectCount">
                    {p.count} {((textObj && textObj.transactions) || 'transactions')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="AccountOverview-volumeSection">
          <div className="AccountOverview-sectionTitle">{(textObj && textObj.transactionVolume) || 'Transaction volume'}</div>
          {!hasVolume ? (
            <div className="AccountOverview-noData">{(textObj && textObj.noData) || 'No data'}</div>
          ) : (
            <div className="AccountOverview-volumeChart">
              {function() {
                var W = Math.max(1, (volume.series.length - 1) * 24 + 40);
                var H = 200;
                var leftPad = 52;
                var rightPad = 20;
                var topPad = 16;
                var bottomPad = 32;

                var rect = (this.volumeSvgRef && this.volumeSvgRef.current) ? this.volumeSvgRef.current.getBoundingClientRect() : null;
                if (rect) {
                  var idealH = (W * rect.height) / rect.width;
                  H = Math.max(160, Math.min(420, idealH));
                }

                var scale = rect ? Math.min(rect.width / W, rect.height / H) : 1;
                var dotPixelRadius = 5;
                var dotR = dotPixelRadius / scale;
                var outlinePx = 2;
                var outlineVb = outlinePx / scale;
                topPad = 18 + dotR + outlineVb;
                bottomPad = 24 + dotR + outlineVb;

                var rawMax = Math.max(volume.max || 0, 1);
                var tickCount = 4;
                var rawStep = rawMax / tickCount;
                var magnitude = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
                var normalised = rawStep / magnitude;
                var niceNormalised = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
                var niceStep = niceNormalised * magnitude;
                if (!niceStep || !isFinite(niceStep)) niceStep = 1;
                var maxTick = Math.ceil(rawMax / niceStep) * niceStep;
                var chartMax = Math.max(maxTick, niceStep);
                if (!chartMax || !isFinite(chartMax)) {
                  chartMax = 1;
                }
                var ticks = [];
                for (var tick = 0; tick <= chartMax + 0.0001; tick += niceStep) {
                  ticks.push(Number(tick.toFixed(2)));
                }
                if (ticks.length === 0) {
                  ticks = [0, chartMax];
                }

                function formatAxisLabel(value) {
                  if (value >= 1000000) {
                    var millions = value / 1000000;
                    return 'HK$ ' + (millions >= 10 ? Math.round(millions) : millions.toFixed(1)) + 'M';
                  }
                  if (value >= 1000) {
                    var thousands = value / 1000;
                    return 'HK$ ' + (thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1)) + 'K';
                  }
                  return 'HK$ ' + Math.round(value);
                }

                function xPos(i) {
                  if (volume.series.length <= 1) return leftPad;
                  var span = W - leftPad - rightPad;
                  return leftPad + (span * i) / (volume.series.length - 1);
                }
                function yPos(val) {
                  if (!chartMax) return H - bottomPad;
                  var span = H - topPad - bottomPad;
                  var clamped = Math.max(0, Math.min(val, chartMax));
                  return topPad + (1 - clamped / chartMax) * span;
                }

                function buildSeriesPoints(selector) {
                  var pts = [];
                  for (var i = 0; i < volume.series.length; i++) {
                    pts.push({ x: xPos(i), y: yPos(selector(volume.series[i])) });
                  }
                  return pts;
                }

                function buildSmoothPath(points) {
                  var n = points.length;
                  if (n === 0) return '';
                  if (n === 1) {
                    return 'M' + points[0].x + ' ' + points[0].y;
                  }
                  var smoothing = 0.15;
                  var yMin = topPad;
                  var yMax = H - bottomPad;
                  var d = 'M' + points[0].x + ' ' + points[0].y;

                  function controlPoint(current, previous, next, reverse) {
                    var p = previous || current;
                    var npt = next || current;
                    var dx = npt.x - p.x;
                    var dy = npt.y - p.y;
                    var angle = Math.atan2(dy, dx) + (reverse ? Math.PI : 0);
                    var len = Math.sqrt(dx * dx + dy * dy) * smoothing;
                    return {
                      x: current.x + Math.cos(angle) * len,
                      y: current.y + Math.sin(angle) * len,
                    };
                  }

                  for (var i2 = 1; i2 < n; i2++) {
                    var curr = points[i2];
                    var prev = points[i2 - 1];
                    var next = points[i2 + 1];
                    var cp1 = controlPoint(prev, points[i2 - 2], curr, false);
                    var cp2 = controlPoint(curr, prev, next, true);
                    var minY1 = Math.min(prev.y, curr.y);
                    var maxY1 = Math.max(prev.y, curr.y);
                    cp1.y = Math.max(minY1, Math.min(maxY1, cp1.y));
                    cp1.y = Math.max(yMin, Math.min(yMax, cp1.y));

                    var nextY = (next && next.y !== undefined) ? next.y : curr.y;
                    var minY2 = Math.min(curr.y, nextY);
                    var maxY2 = Math.max(curr.y, nextY);
                    cp2.y = Math.max(minY2, Math.min(maxY2, cp2.y));
                    cp2.y = Math.max(yMin, Math.min(yMax, cp2.y));

                    curr.y = Math.max(yMin, Math.min(yMax, curr.y));
                    prev.y = Math.max(yMin, Math.min(yMax, prev.y));
                    d += ' C' + cp1.x + ' ' + cp1.y + ', ' + cp2.x + ' ' + cp2.y + ', ' + curr.x + ' ' + curr.y;
                  }
                  return d;
                }

                var allPoints = buildSeriesPoints(function(d) { return d.all; });
                var payPoints = buildSeriesPoints(function(d) { return d.payment; });
                var donationPoints = buildSeriesPoints(function(d) { return d.donation; });

                var pathAll = buildSmoothPath(allPoints);
                var pathPay = buildSmoothPath(payPoints);
                var pathDon = buildSmoothPath(donationPoints);

                var baseLineY = H - bottomPad;
                function buildArea(path, points) {
                  if (!path || !points.length) return '';
                  var last = points[points.length - 1];
                  var first = points[0];
                  return (
                    path +
                    ' L' + last.x + ' ' + baseLineY +
                    ' L' + first.x + ' ' + baseLineY +
                    ' Z'
                  );
                }

                var areaAll = buildArea(pathAll, allPoints);
                var areaPay = buildArea(pathPay, payPoints);
                var areaDon = buildArea(pathDon, donationPoints);

                var hoverIdx = this.state.chartHoverIndex;
                var hoverX = null;
                if (hoverIdx !== null && hoverIdx >= 0 && hoverIdx < volume.series.length) {
                  hoverX = xPos(hoverIdx);
                }

                var vs = this.state.visibleSeries || { all: true, payment: true, donation: true };
                var gradientIdAll = 'AccountOverviewGradientAll';
                var gradientIdPayment = 'AccountOverviewGradientPayment';
                var gradientIdDonation = 'AccountOverviewGradientDonation';

                return (
                  <svg
                    viewBox={'0 0 ' + W + ' ' + H}
                    preserveAspectRatio="xMidYMid meet"
                    className="AccountOverview-lineSvg"
                    ref={this.volumeSvgRef}
                    onMouseMove={(e) => this.handleChartMouseMove(e, W, H, leftPad, rightPad, topPad, bottomPad, volume.series.length)}
                    onMouseLeave={this.handleChartMouseLeave}
                    role="img"
                    aria-label={(textObj && textObj.transactionVolumeChartAria) || 'Transaction volume trends'}
                  >
                    <defs>
                      <linearGradient id={gradientIdAll} x1="0" y1={topPad} x2="0" y2={baseLineY} gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#111827" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#111827" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id={gradientIdPayment} x1="0" y1={topPad} x2="0" y2={baseLineY} gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#2b91d6" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#2b91d6" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id={gradientIdDonation} x1="0" y1={topPad} x2="0" y2={baseLineY} gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#a855f7" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
                      </linearGradient>
                    </defs>

                    <g className="AccountOverview-gridGroup">
                      {ticks.map(function(tickValue, idx) {
                        var y = yPos(tickValue);
                        var isZero = Math.abs(tickValue) < 0.0001;
                        return (
                          <g key={idx} className="AccountOverview-gridRow">
                            <line
                              x1={leftPad}
                              x2={W - rightPad}
                              y1={y}
                              y2={y}
                              className={'AccountOverview-gridLine' + (isZero ? ' baseline' : '')}
                            />
                            <text
                              x={leftPad - 10}
                              y={y}
                              className="AccountOverview-yAxisLabel"
                              textAnchor="end"
                              dominantBaseline="middle"
                            >
                              {formatAxisLabel(tickValue)}
                            </text>
                          </g>
                        );
                      })}
                      <line
                        x1={leftPad}
                        x2={leftPad}
                        y1={topPad - 6}
                        y2={baseLineY}
                        className="AccountOverview-axisLine"
                      />
                    </g>

                    {vs.all && areaAll ? (<path d={areaAll} className="AccountOverview-area AccountOverview-area--all" fill={`url(#${gradientIdAll})`} />) : null}
                    {vs.payment && areaPay ? (<path d={areaPay} className="AccountOverview-area AccountOverview-area--payment" fill={`url(#${gradientIdPayment})`} />) : null}
                    {vs.donation && areaDon ? (<path d={areaDon} className="AccountOverview-area AccountOverview-area--donation" fill={`url(#${gradientIdDonation})`} />) : null}

                    {vs.all ? (<path d={pathAll} className="AccountOverview-linePath AccountOverview-linePath--all" />) : null}
                    {vs.payment ? (<path d={pathPay} className="AccountOverview-linePath AccountOverview-linePath--payment" />) : null}
                    {vs.donation ? (<path d={pathDon} className="AccountOverview-linePath AccountOverview-linePath--donation" />) : null}

                    {hoverX !== null ? (
                      <g>
                        <line x1={hoverX} x2={hoverX} y1={topPad} y2={baseLineY} className="AccountOverview-guideLine" />
                        {vs.all ? (
                          <circle cx={hoverX} cy={yPos(volume.series[hoverIdx].all)} r={dotR} className="AccountOverview-dot all" vectorEffect="non-scaling-stroke" />
                        ) : null}
                        {vs.payment ? (
                          <circle cx={hoverX} cy={yPos(volume.series[hoverIdx].payment)} r={dotR} className="AccountOverview-dot payment" vectorEffect="non-scaling-stroke" />
                        ) : null}
                        {vs.donation ? (
                          <circle cx={hoverX} cy={yPos(volume.series[hoverIdx].donation)} r={dotR} className="AccountOverview-dot donation" vectorEffect="non-scaling-stroke" />
                        ) : null}
                      </g>
                    ) : null}
                  </svg>
                );
              }.call(this)}

              <div className="AccountOverview-lineLegend">
                <div className={"AccountOverview-legendItem " + (this.state.visibleSeries.all ? '' : 'inactive')} onClick={() => this.toggleSeriesVisibility('all')}>
                  <span className="AccountOverview-legendDot all"></span>{(textObj && textObj.allTransactions) || 'All transactions'}
                </div>
                <div className={"AccountOverview-legendItem " + (this.state.visibleSeries.payment ? '' : 'inactive')} onClick={() => this.toggleSeriesVisibility('payment')}>
                  <span className="AccountOverview-legendDot payment"></span>{(textObj && textObj.payments) || 'Payments'}
                </div>
                <div className={"AccountOverview-legendItem " + (this.state.visibleSeries.donation ? '' : 'inactive')} onClick={() => this.toggleSeriesVisibility('donation')}>
                  <span className="AccountOverview-legendDot donation"></span>{(textObj && textObj.donations) || 'Donations'}
                </div>
              </div>

              {this.state.chartHoverIndex !== null ? (
                <div className="AccountOverview-tooltip top" style={{ left: this.state.chartHoverLeft }}>
                  <div className="AccountOverview-tooltipDate">{volume.series[this.state.chartHoverIndex].label}</div>
                  {this.state.visibleSeries.all ? (
                    <div className="AccountOverview-tooltipRow"><span className="dot all"></span>HK$ <NumericFormat value={volume.series[this.state.chartHoverIndex].all} displayType="text" valueIsNumericString={true} thousandSeparator={true} decimalScale={2} fixedDecimalScale={true} /></div>
                  ) : null}
                  {this.state.visibleSeries.payment ? (
                    <div className="AccountOverview-tooltipRow"><span className="dot payment"></span>HK$ <NumericFormat value={volume.series[this.state.chartHoverIndex].payment} displayType="text" valueIsNumericString={true} thousandSeparator={true} decimalScale={2} fixedDecimalScale={true} /></div>
                  ) : null}
                  {this.state.visibleSeries.donation ? (
                    <div className="AccountOverview-tooltipRow"><span className="dot donation"></span>HK$ <NumericFormat value={volume.series[this.state.chartHoverIndex].donation} displayType="text" valueIsNumericString={true} thousandSeparator={true} decimalScale={2} fixedDecimalScale={true} /></div>
                  ) : null}
                </div>
              ) : null}

              <div className="AccountOverview-volumeAxis">
                <div className="AccountOverview-axisLabel">{volume.series.length ? volume.series[0].label : ''}</div>
                <div className="AccountOverview-axisLabel">{volume.series.length ? volume.series[Math.floor(volume.series.length / 2)].label : ''}</div>
                <div className="AccountOverview-axisLabel">{volume.series.length ? volume.series[volume.series.length - 1].label : ''}</div>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  fetchMorePayments() {
    const { selectedProject } = this.state;

    // Return early if:
    // 1. Currently fetching more payments
    // 2. No more payments to fetch
    // 3. LoadingList is true (initial load in progress)
    // 4. No data loaded yet
    if (
      selectedProject.isFetchingMore ||
      !selectedProject.hasMore ||
      selectedProject.loadingList ||
      !selectedProject.paymentsList.length
    ) {
      return;
    }

    // Set fetching flag
    this.setState({
      selectedProject: {
        ...selectedProject,
        isFetchingMore: true,
      }
    });

    const sortField = this.state.paymentSortField || ORG_PAYMENT_LIST_SORT;
    const sortOrder = this.state.paymentSortOrder || 'desc';
    const skipBasedSorts = ['organizationReceiptId', 'donorName', 'donorEmail', 'phoneNumber', 'amount'];
    const useSkip = skipBasedSorts.includes(sortField);

    const reqBody = {
      limit: ORG_PAYMENT_LIST_LIMIT,
      sort: sortField,
      sortOrder,
    };

    if (useSkip) {
      reqBody.skip = selectedProject.paymentsList.length;
    } else {
      const cursorKey = 'last' + sortField.charAt(0).toUpperCase() + sortField.slice(1);
      reqBody[cursorKey] = selectedProject.lastSortValue;
    }

    if (selectedProject.projectId) {
      reqBody.projectId = selectedProject.projectId;
    }
    if (selectedProject.searchTerm) {
      reqBody.searchTerm = selectedProject.searchTerm;
    }
    if (this.state.paymentModeFilter) reqBody.paymentMode = this.state.paymentModeFilter;
    if (this.state.paymentStatusFilter) {
      reqBody.status = this.state.paymentStatusFilter;
    }
    if (this.state.paymentTransactionTypeFilter) {
      reqBody.transactionType = this.state.paymentTransactionTypeFilter;
    }
    if (this.state.paymentDateRangeStart && this.state.paymentDateRangeEnd) {
      const startNormalized = new Date(this.state.paymentDateRangeStart);
      startNormalized.setHours(0, 0, 0, 0);
      const endNormalized = new Date(this.state.paymentDateRangeEnd);
      endNormalized.setHours(23, 59, 59, 999);
      reqBody.startDate = startNormalized.toISOString();
      reqBody.endDate = endNormalized.toISOString();
      reqBody.dateField = this.state.paymentDateField || 'scheduledDate';
    }

    simpleFetch("/organizations/payments", "GET", reqBody).then((res) => {
      if (res.status === 200 && res.body.length > 0) {
        const lastItem = res.body[res.body.length - 1];
        const currentList = this.state.selectedProject.paymentsList || [];
        const existingIds = new Set(currentList.map((p) => p && p.id).filter((id) => id != null));
        const newPayments = res.body
          .map((payment) => ({ ...payment, clicked: false }))
          .filter((payment) => payment.id == null || !existingIds.has(payment.id));

        this.setState({
          selectedProject: {
            ...this.state.selectedProject,
            paymentsList: [...currentList, ...newPayments],
            lastId: lastItem.id,
            lastUpdatedAt: lastItem.updatedAt,
            lastSortValue: lastItem[sortField] || lastItem.updatedAt,
            hasMore: res.body.length === ORG_PAYMENT_LIST_LIMIT,
            isFetchingMore: false
          }
        });
      } else {
        // No more results
        this.setState({
          selectedProject: {
            ...selectedProject,
            hasMore: false,
            isFetchingMore: false
          }
        });
      }
    }).catch(() => {
      // Handle error
      this.setState({
        selectedProject: {
          ...selectedProject,
          isFetchingMore: false
        }
      });
    });
  }

  async refreshTransactionReview(id) {
    try {
      const response = await simpleFetch('/organizations/payments/' + id, 'GET');
      if (response.status !== 200 || response.body.id !== id) return;
      this.setState(previous => ({ selectedProject: { ...previous.selectedProject,
        paymentsList: previous.selectedProject.paymentsList.map(payment =>
          payment.id === id ? { ...payment, ...response.body, stripeManualReview: response.body.stripeManualReview, paymeManualReview: response.body.paymeManualReview } : payment)
      } }));
    } catch (_) { /* Keep the current list until the next successful refresh. */ }
  }

  renderProjectPaymentItem(data, func, textObj) {
    if (!data) return;
    const stripeWarning = (data.PaymentMode === 'Stripe' && data.stripeManualReview?.changes?.length > 0) ||
      (data.PaymentMode === 'PayMe' && data.paymeManualReview?.changes?.length > 0);
    const stripeWarningLabel = data.PaymentMode === 'PayMe' ? this.props.languageObj.paymeReconciliation.listWarning : this.props.languageObj.stripeReconciliation.listWarning;
    const indicatorClass = stripeWarning ? 'AccountOverview-stripeReviewIndicator' :
      data.status === 'complete' ? 'AccountOverview-textGroupIndicator-success' :
        data.status === 'expired' ? 'AccountOverview-textGroupIndicator-expired' :
          data.status === 'error' ? 'AccountOverview-textGroupIndicator-error' :
            ['pending_for_confirmation', 'pending'].includes(data.status) && data.PaymentMode === 'Manual' ? 'AccountOverview-textGroupIndicator-manual-pending' :
              ['pending_for_confirmation', 'pending'].includes(data.status) ? 'AccountOverview-textGroupIndicator-pending' :
                data.status === 'reauthenticate' ? 'AccountOverview-textGroupIndicator-reauthenticate' : '';


    // Use scheduledDate (transaction date) when available, otherwise fall back to updatedAt
    var dateToShow = data.scheduledDate
      ? new Date(data.scheduledDate)
      : new Date(data.updatedAt);
    var dateComponents = [
      dateToShow.getDate(),
      dateToShow.getMonth() + 1,
      dateToShow.getFullYear(),
      dateToShow.getHours(),
      (dateToShow.getMinutes() < 10 ? "0" : "") + dateToShow.getMinutes(),
    ];
    var itemDate = (
      <div>
        {dateComponents[2]}/{dateComponents[1]}/{dateComponents[0]}{" "}
        {dateComponents[3]}:{dateComponents[4]}
      </div>
    );

    function clickedItem() {
      func.onPaymentClicked(data);
    }

    // console.log(JSON.stringify(data) + " -505");

    const projectLabel = getPaymentProjectLabel(
      func.props.language,
      data,
      func.state.missions
    );

    const projectName = projectLabel ? (
      <Fragment>
        <br />
        {projectLabel}
      </Fragment>
    ) : null;

    if (data.clicked) {
      const languageObj = this.props.languageObj;
      /*    const color = {
        background: '#' + this.state.organizationObj.headerColor
      };
*/
      return (
        <div
          className="AccountOverview-orgItemActive"
          key={"paymentList" + data.id}
        >
          <div className="AccountOverview-textGroup">
            <div className={`AccountOverview-textGroupIndicator ${indicatorClass}`} title={stripeWarning ? stripeWarningLabel : undefined}
              aria-label={stripeWarning ? stripeWarningLabel : undefined} role={stripeWarning ? 'img' : undefined}
              tabIndex={stripeWarning ? 0 : undefined}>{stripeWarning && <span aria-hidden="true">!</span>}</div>
            <div className="AccountOverview-dateActive">{itemDate}</div>
            <div className="AccountOverview-orgTextActive">
              {data.donorOrganizationName || [data.donorFirstName, data.donorLastName].map(s => (s || '').trim()).filter(Boolean).join(' ') || ''}
              {projectName}
              {data.paymeReview?.status === 'required' && <small className="AccountOverview-paymeReviewTag">{languageObj.paymeReconciliation.paidReviewList}</small>}
            </div>
            <div className="AccountOverview-amountActive">
              {data.organizationReceiptId && (
                <>
                  {data.organizationReceiptId}
                  {data.receiptVoidedAt && <small> · {languageObj.ProjectPaymentList.PaymentDetail.receiptVoid.voided}</small>}
                  <br />
                </>
              )}
              HK
              <NumericFormat
                decimalSeparator="."
                displayType="text"
                valueIsNumericString={true}
                value={data.amount}
                allowNegative={false}
                thousandSeparator={true}
                prefix={"$"}
                fixedDecimalScale={true}
                decimalScale={2}
              />
            </div>
          </div>
          <div className="AccountOverview-arrowActive"></div>
        </div>
      );
    }

    const languageObj = this.props.languageObj;

    return (
      <div
        className="AccountOverview-orgItem"
        key={"paymentList" + data.id}
        onClick={clickedItem}
      >
        <div className="AccountOverview-textGroup">
          <div className={`AccountOverview-textGroupIndicator ${indicatorClass}`} title={stripeWarning ? stripeWarningLabel : undefined}
            aria-label={stripeWarning ? stripeWarningLabel : undefined} role={stripeWarning ? 'img' : undefined}
            tabIndex={stripeWarning ? 0 : undefined}>{stripeWarning && <span aria-hidden="true">!</span>}</div>
          <div className="AccountOverview-date">{itemDate}</div>
          <div className="AccountOverview-orgText">
            {data.donorOrganizationName || [data.donorFirstName, data.donorLastName].map(s => (s || '').trim()).filter(Boolean).join(' ') || ''}
            {projectName}
            {data.paymeReview?.status === 'required' && <small className="AccountOverview-paymeReviewTag">{languageObj.paymeReconciliation.paidReviewList}</small>}
          </div>
          <div className="AccountOverview-amount">
            {data.organizationReceiptId && (
              <>
                {data.organizationReceiptId}
                {data.receiptVoidedAt && <small> · {languageObj.ProjectPaymentList.PaymentDetail.receiptVoid.voided}</small>}
                <br />
              </>
            )}
            HK
            <NumericFormat
              decimalSeparator="."
              displayType="text"
              valueIsNumericString={true}
              value={data.amount}
              allowNegative={false}
              thousandSeparator={true}
              prefix={"$"}
              fixedDecimalScale={true}
              decimalScale={2}
            />
          </div>
        </div>
        <div className="AccountOverview-arrow"></div>
      </div>
    );
  }

  renderProjectPaymentList(textObj, toolbarContent, filterSortContent) {
    if (!textObj) return null;

    const payments = this.state.selectedProject.paymentsList || [];
    const searchTerm = (this.state.paymentSearchTerm || '').trim().toLowerCase();

    const filteredPayments = searchTerm
      ? payments.filter((payment) => {
        // Build searchable text from all fields (trimmed and normalized)
        const searchableText = [
          payment.donorFirstName,
          payment.donorLastName,
          payment.donorEmail,
          payment.donorOrganizationName,
          payment.projectName,
          payment.organizationReceiptId,
          payment.memo,
          payment.wallet,
          payment.cardBrand,
          payment.cardLast4 ? `****${payment.cardLast4}` : '',
          payment.status,
          payment.organizationViewId,
          payment.stripePaymentIntentId,
          payment.amount != null ? String(payment.amount) : '',
          payment.id != null ? String(payment.id) : '',
          payment.amount != null ? `HK$ ${payment.amount}` : '',
        ]
          .filter(Boolean)
          .map(v => String(v).trim())
          .join(' ')
          .toLowerCase();

        // Split search term into words
        const searchWords = searchTerm.split(/\s+/).filter(Boolean);

        // ALL search words must appear in searchable text (word-order flexible)
        return searchWords.every(word => searchableText.includes(word));
      })
      : payments;

    // Sort order is controlled by the backend via sort/sortOrder params
    const sortedPayments = filteredPayments;

    let listContent;

    if (this.state.selectedProject.loadingList) {
      listContent = (
        <div className="AccountOverview-emptyPage1">
          <div className="AccountOverview-center">
            <div className="AccountOverview-btm-load"></div>
          </div>
        </div>
      );
    } else if (!payments.length) {
      listContent = (
        <div className="AccountOverview-emptyPage1">
          <div className="AccountOverview-noTransactionsFound">
            <div className="AccountOverview-noTransactionsFoundText">
              {textObj.noTransactionsFoundText}
            </div>
          </div>
        </div>
      );
    } else if (searchTerm && !sortedPayments.length) {
      listContent = (
        <div className="AccountOverview-emptyPage1">
          <div className="AccountOverview-noTransactionsFound">
            <div className="AccountOverview-noTransactionsFoundText">
              {textObj.noSearchResultsText || 'No matching transactions'}
            </div>
          </div>
        </div>
      );
    } else {
      listContent = (
        <Fragment>
          <div className="AccountOverview-spacingVertical" />
          {sortedPayments.map((payment) =>
            this.renderProjectPaymentItem(
              payment,
              this,
              textObj.ProjectPaymentItem
            )
          )}
        </Fragment>
      );
    }

    return (
      <div className="AccountOverview-orgContainer">
        <div className="AccountOverview-listPane">
          {toolbarContent}
          <div
            className="AccountOverview-orgList"
            ref={this.paymentListRef}
          >
            {filterSortContent}
            {listContent}

            {this.state.selectedProject.isFetchingMore && sortedPayments.length > 0 && (
              <div className="AccountOverview-loadingMore">
                <div className="AccountOverview-btm-load"></div>
              </div>
            )}
          </div>
        </div>

      </div>
    );
  }

  renderAddPaymentButton() {
    if (this.state.showDashboard) return null;

    const project = this.state.selectedProject || {};
    if (!project || project.projectStatus === 'closed') return null;

    const orgPlan = (this.state.organizationObj && this.state.organizationObj.currentPlan)
      ? this.state.organizationObj.currentPlan.toLowerCase()
      : "";
    const canAddOfflinePayments = orgPlan === "premium";
    const languageObj = this.props.languageObj || {};
    const upgradeHint = languageObj.addOfflinePaymentsUpgradeHint || "Upgrade to Premium to Add Offline Payments";

    const button = (
      <div
        className={`AccountOverview-paymentBtnContainer${canAddOfflinePayments ? "" : " AccountOverview-paymentBtnContainer--disabled"}`}
        title={canAddOfflinePayments ? undefined : upgradeHint}
        data-tooltip={canAddOfflinePayments ? undefined : upgradeHint}
      >
        <div
          data-testid="accountoverview-add-payment-btn"
          className={`AccountOverview-addTrxBtn${canAddOfflinePayments ? "" : " AccountOverview-addTrxBtn--disabled"}`}
          onClick={canAddOfflinePayments ? this.onAddPaymentClick : undefined}
          role="button"
          aria-disabled={!canAddOfflinePayments}
          tabIndex={canAddOfflinePayments ? 0 : -1}
        >
          <div className="AccountOverview-addTrxIcn" />
        </div>
      </div>
    );

    return button;
  }

  renderFinalCard() {
    if (!this.state.hasMore) return;

    if (this.state.fetching) {
      return (
        <div className="AccountOverview-loadContainer">
          <div className="AccountOverview-load"></div>
        </div>
      );
    }
    return (
      <div className="AccountOverview-loadContainer">
        <div
          className="AccountOverview-loadonPress"
          onClick={this.fetchData}
        ></div>
      </div>
    );
  }


  renderPaymentDetail(func, textObj) {
    if (!this.state.paymentDetail) return;

    const data = this.state.paymentDetail;
    const languageStrings = (func.props && func.props.languageObj) || {};
    const upgradeHintText = languageStrings.upgradeOfflinePaymentsHint || 'Upgrade to Premium to update Offline Payments';

    function onBackPaymentDetailBtn() {
      var paymentData = func.state.selectedProject;

      var items = paymentData.paymentsList;

      for (var i = 0; i < items.length; i++) {
        items[i].clicked = false;
      }

      paymentData.paymentsList = items;

      func.setState({
        paymentDetail: null,
        selectedProject: paymentData,
      });
    }

    function onDeletePaymentBtn(id) {
      const reqBody = {
        paymentId: id,
      };

      const paymentItem = func.state.selectedProject;

      func.setState({
        selectedProject: {
          ...paymentItem,
          loadingList: true,
        },
      });

      simpleFetch('/organizations/payments/delete', 'POST', reqBody).then(res => {
        if (res.status === 200) {
          let projectData = null;

          if (paymentItem.projectId) {
            projectData = {
              id: paymentItem.projectId
            };
          }

          func.fetchPaymentsData(projectData, true, false);
        } else {
          func.setState({
            selectedProject: {
              ...paymentItem,
              loadingList: false,
            },
          });
        }
      });
    }

    function onSavePaymentBtn(id, formData, isProjectChanged, isQuickPayment) {

      const reqBody = {
        paymentId: id,
        ...formData,
      };

      const paymentItem = func.state.selectedProject;

      func.setState({
        selectedProject: {
          ...paymentItem,
          loadingList: true,
        },
      });

      simpleFetch('/organizations/payments/edit', 'POST', reqBody).then(res => {
        if (res.status === 200) {
          // only preverse payment detail if project is not changed
          const preservePaymentDetail = !isProjectChanged;

          let projectData = {
            id: formData.projectId
          };

          if (isQuickPayment) {
            projectData = null;
          }

          // Bump refresh key so the side sheet re-fetches the payment detail
          // even when its `id` prop hasn't changed (e.g. edit on the same row).
          if (preservePaymentDetail) {
            func.setState((prev) => ({
              paymentDetailRefreshKey: (prev.paymentDetailRefreshKey || 0) + 1,
            }));
          }

          func.fetchPaymentsData(
            projectData,
            true,
            preservePaymentDetail
          );
        } else {
          func.setState({
            selectedProject: {
              ...paymentItem,
              loadingList: false,
            },
          });
        }
      });
    }

    function onChangePaymentStatusBtn(id, status, sendReceipt, text = '') {

      const reqBody = {
        paymentId: id,
        status,
        sendReceipt,
        declinedReason: text,
      };

      const paymentItem = func.state.selectedProject;

      func.setState({
        selectedProject: {
          ...paymentItem,
          loadingList: true,
        },
      });

      simpleFetch('/organizations/payments/change-payment-status', 'POST', reqBody).then(res => {
        if (res.status === 200) {
          let projectData = null;

          if (paymentItem.projectId) {
            projectData = {
              id: paymentItem.projectId
            };
          }

          // Bump refresh key so the side sheet re-fetches the updated payment.
          // The list refetch may exclude the now-different-status payment when
          // a status filter is active, leaving the detail's `id` prop the same
          // and the cached state stale.
          func.setState((prev) => ({
            paymentDetailRefreshKey: (prev.paymentDetailRefreshKey || 0) + 1,
          }));

          func.fetchPaymentsData(
            projectData,
            true,
            true
          );
        } else {
          func.setState({
            selectedProject: {
              ...paymentItem,
              loadingList: false,
            },
          });
        }
      });
    }

    return (
      <RenderPaymentDetail
        organizationObj={func.state.organizationObj}
        missions={func.state.missions}
        onEnsureAllProjects={func.loadAllProjects}
        onVerificationChange={id => func.refreshTransactionReview(id)}
        textObj={textObj}
        language={func.props.language}
        id={data.id}
        refreshKey={func.state.paymentDetailRefreshKey}
        onBackBtn={onBackPaymentDetailBtn}
        onDeleteBtn={id => onDeletePaymentBtn(id)}
        onSaveBtn={
          (id, formData, isProjectChanged, isQuickPayment) => 
            onSavePaymentBtn(id, formData, isProjectChanged, isQuickPayment)
        }
        onChangeStatusBtn={
          (id, status, sendReceipt, text) => 
            onChangePaymentStatusBtn(id, status, sendReceipt, text)
        }
        upgradeHint={upgradeHintText}
      />
    );
  }

  navigateToStripe() {
    this.props.history.push({ pathname: "/OrgSettings", page: 5 });
  }

  onHomeBtnClick() {
    if (window) {
      window.location.reload();
    }
  }

  renderProjectFilterControls(languageObj, options = {}) {
    const { className = "", disabled = false } = options;
    return (
      <div className={`AccountOverview-projectFilterCard${className ? ` ${className}` : ""}`}>
        <div className="AccountOverview-searchWrapper">
          <img
            src={searchIcon}
            alt=""
            aria-hidden="true"
            className="AccountOverview-searchIcon"
          />
          <input
            type="search"
            className="AccountOverview-searchInput"
            data-testid="accountoverview-project-search"
            placeholder={
              languageObj.projectSearchPlaceholder || "Search projects"
            }
            aria-label={
              languageObj.projectSearchAriaLabel ||
              languageObj.projectSearchPlaceholder ||
              "Search projects"
            }
            value={this.state.projectSearchTerm}
            onChange={this.handleProjectSearchChange}
            disabled={disabled}
          />
        </div>
        <select
          className="AccountOverview-statusFilter"
          value={this.state.projectStatusFilter}
          onChange={this.handleProjectStatusFilterChange}
          aria-label={
            languageObj.projectStatusAriaLabel || "Filter by status"
          }
          disabled={disabled}
        >
          <option value="">
            {languageObj.projectStatusAllLabel || "All statuses"}
          </option>
          <option value="open">
            {languageObj.projectStatusOpenLabel || "Open"}
          </option>
          <option value="unlisted">
            {languageObj.projectStatusUnlistedLabel || "Unlisted"}
          </option>
          <option value="closed">
            {languageObj.projectStatusClosedLabel || "Closed"}
          </option>
        </select>
        {this.state.isLoadingAllProjects ? (
          <div className="AccountOverview-projectFilterLoading">
            <div className="AccountOverview-load AccountOverview-load--small"></div>
          </div>
        ) : null}
      </div>
    );
  }

  renderAllTransactionsNav(languageObj) {
    return (
      <div
        className={`AccountOverview-allTransactions${!this.state.selectedProject.projectId ? ' AccountOverview-allTransactions--active' : ''}`}
        onClick={this.onAllTransactionsClicked}
      >
        <div className="AccountOverview-allTransactionsInner">
          <span>{languageObj.allTransactions || 'All Transactions'}</span>
          {!this.state.selectedProject.projectId && (
            <div className="AccountOverview-indicator" />
          )}
        </div>
      </div>
    );
  }

  // Combined action bar: +, search, status and reorder merged into one pill.
  // Shared by the manager toolbar and the desktop sidebar compact list.
  // Reorder toolbar (Back / Save) shown while reordering — shared by the
  // manager and the desktop sidebar so both behave the same on reorder.
  renderReorderControls(languageObj) {
    const isSavingOrder = this.state.isSavingProjectOrder;
    return (
      <Fragment>
        <button
          type="button"
          className="AccountOverview-projectManagerBack"
          onClick={this.cancelProjectReorder}
          disabled={isSavingOrder}
        >
          <span
            className="AccountOverview-projectManagerBackIcn"
            aria-hidden="true"
          />
          <span>{languageObj.projectManagerBack || "Back"}</span>
        </button>

        <button
          type="button"
          className="AccountOverview-projectManagerDone AccountOverview-projectManagerFinish"
          onClick={this.handleProjectReorderToggle}
          disabled={isSavingOrder}
        >
          {isSavingOrder ? (
            <span className="AccountOverview-projectManagerSaving">
              <span className="AccountOverview-projectManagerSpinner" />
              {languageObj.projectManagerSaving || "Saving..."}
            </span>
          ) : (
            languageObj.projectManagerFinish || "Save"
          )}
        </button>
      </Fragment>
    );
  }

  // Cards / List segmented toggle (styled like the mobile tabs). Cards = the
  // sidebar card list; List = the expanded table takeover. Shown in the sidebar
  // (Cards active) and in the expanded manager's header (List active), so it
  // switches back and forth. Desktop only (hidden on mobile via CSS).
  renderProjectViewTabs(languageObj) {
    const isList = this.state.showProjectTable;
    return (
      <div
        className="AccountOverview-sidebarViewTabs"
        role="tablist"
        aria-label={languageObj.sidebarViewAriaLabel || 'Project view'}
      >
        <button
          type="button"
          role="tab"
          aria-selected={!isList}
          className={`AccountOverview-sidebarViewTab${!isList ? ' AccountOverview-sidebarViewTab--active' : ''}`}
          onClick={() => {
            if (this.state.showProjectTable) this.closeProjectManager();
          }}
        >
          {languageObj.sidebarViewCards || 'Overview'}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isList}
          className={`AccountOverview-sidebarViewTab${isList ? ' AccountOverview-sidebarViewTab--active' : ''}`}
          onClick={() => {
            if (!this.state.showProjectTable) this.toggleProjectTable();
          }}
        >
          {languageObj.sidebarViewList || 'List'}
        </button>
      </div>
    );
  }

  renderCombinedActionBar(languageObj) {
    return (
      <div className="AccountOverview-managerActionBar">
        <div
          className="AccountOverview-addIcnContainer"
          onClick={this.createProjectBtn}
          data-testid="accountoverview-create-project-btn"
          title={languageObj.projectManagerAdd || "Add project"}
          aria-label={languageObj.projectManagerAdd || "Add project"}
          role="button"
          tabIndex={0}
        >
          <div className="AccountOverview-addIcn" />
        </div>
        {this.renderProjectFilterControls(languageObj, {
          className: "AccountOverview-projectFilterCard--manager",
        })}
        <div
          className={`AccountOverview-sortIcnContainer${
            this.state.isFrameEdit
              ? " AccountOverview-sortIcnContainer--active"
              : ""
          }`}
          onClick={this.handleProjectReorderToggle}
          title={
            this.state.isFrameEdit
              ? languageObj.projectManagerFinish || "Save"
              : languageObj.projectManagerReorder || "Reorder"
          }
          aria-label={
            this.state.isFrameEdit
              ? languageObj.projectManagerFinish || "Save"
              : languageObj.projectManagerReorder || "Reorder"
          }
          role="button"
          tabIndex={0}
        >
          <div className="AccountOverview-sortIcn" />
        </div>
      </div>
    );
  }

  renderProjectManager(languageObj) {
    const isReorder = this.state.isFrameEdit;
    const view = this.state.projectManagerView || "cards";
    const filteredMissions = this.getFilteredMissions();

    return (
      <div
        className={`AccountOverview-projectManager${
          this.state.projectManagerVisible
            ? " AccountOverview-projectManager--visible"
            : ""
        }`}
      >
        <div className="AccountOverview-projectManagerHeader">
          <div className="AccountOverview-projectManagerTitleRow">
            <h2 className="AccountOverview-projectManagerTitle">
              {languageObj.projectManagerTitle || "Projects"}
            </h2>
            {/* Cards / List toggle also lives here so you can switch back to the
                sidebar Cards view. Clicking Cards minimizes the takeover. */}
            {!isReorder ? this.renderProjectViewTabs(languageObj) : null}
          </div>

          <div
            className={`AccountOverview-projectManagerToolbar${
              isReorder
                ? " AccountOverview-projectManagerToolbar--reorder"
                : ""
            }`}
          >
            {isReorder ? (
              this.renderReorderControls(languageObj)
            ) : (
              // +, search, status and reorder combined into one pill.
              this.renderCombinedActionBar(languageObj)
            )}
          </div>
        </div>

        <div className="AccountOverview-projectManagerBody">
          {/* Mobile: the "All Transactions" nav sits just below the status
              filter (top of the body), matching the desktop compact-list order.
              Hidden while reordering. */}
          {this.state.isMobile && !isReorder
            ? this.renderAllTransactionsNav(languageObj)
            : null}
          {view === "cards" ? (
            <div
              className="AccountOverview-projectManagerCards"
              onDragLeave={
                isReorder ? this.handleProjectDragLeaveList : undefined
              }
              onDrop={isReorder ? this.handleProjectDrop : undefined}
              onDragOver={
                isReorder
                  ? (e) => {
                    if (this.state.dragProjectId != null) e.preventDefault();
                  }
                  : undefined
              }
            >
              {filteredMissions.map((mission, index) => (
                <Fragment key={mission.id}>
                  {isReorder && this.state.dropProjectIndex === index ? (
                    <div className="AccountOverview-projectDropIndicator" />
                  ) : null}
                  <ProjectDetails
                    isFrameEdit={isReorder}
                    isDragging={
                      String(this.state.dragProjectId) === String(mission.id)
                    }
                    mission={mission}
                    progressBarColor={this.state.organizationObj.progressBarColor}
                    onProjectClick={this.onProjectClicked}
                    onProjectSettingClick={this.onProjectSettingClick}
                    languageObj={languageObj.ProjectDetails}
                    language={this.props.language}
                    leftArrowBtn={this.leftArrowBtn}
                    rightArrowBtn={this.rightArrowBtn}
                    onDragStart={this.handleProjectDragStart}
                    onDragOver={this.handleProjectDragOver}
                    onDragEnd={this.handleProjectDragEnd}
                    index={index}
                  />
                </Fragment>
              ))}
              {isReorder &&
              this.state.dropProjectIndex === filteredMissions.length ? (
                  <div className="AccountOverview-projectDropIndicator" />
                ) : null}
              {filteredMissions.length === 0 &&
              this.state.missions.length > 0 &&
              !this.state.isLoadingAllProjects ? (
                  <div className="AccountOverview-projectEmptyState AccountOverview-projectEmptyState--manager">
                    {languageObj.projectEmptyState ||
                    "No projects match your search."}
                  </div>
                ) : null}
              {this.state.isLoadingAllProjects ? (
                <div className="AccountOverview-projectManagerLoading">
                  <div className="AccountOverview-load"></div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="AccountOverview-projectTableContainer">
              <ProjectTable
                missions={filteredMissions}
                isLoading={this.state.isLoadingAllProjects}
                language={this.props.language}
                languageObj={languageObj.ProjectTable || {}}
                subDomain={
                  this.state.organizationObj &&
                  this.state.organizationObj.subDomain
                }
                canUseMultiLanguage={
                  (
                    (this.state.organizationObj &&
                      this.state.organizationObj.currentPlan) ||
                    ""
                  ).toLowerCase() === "premium"
                }
                onStatusChange={this.handleProjectStatusChange}
                onBulkUpdate={this.handleBulkUpdate}
                onFieldSave={this.handleProjectFieldSave}
                onEditClick={this.onProjectSettingClick}
                onManageImages={this.openMediaGallery}
                manageImagesLabel={languageObj.manageImages || "Manage Images"}
              />
            </div>
          )}

          {this.state.showMediaGallery ? (
            <MediaGalleryModal
              mode="manage"
              title={languageObj.manageImages || "Manage Images"}
              languageObj={languageObj.ProjectTable || {}}
              onClose={this.closeMediaGallery}
            />
          ) : null}
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.organizationObj || this.state.loadingPage) {
      return (
        <div className="AccountOverview-emptyPage">
          <div className="AccountOverview-center">
            <div className="AccountOverview-btm-load"></div>
          </div>
        </div>
      );
    }

    // console.log(JSON.stringify(this.state) + " -731");


    const languageObj = this.props.languageObj;
    /*
    const headerOverrideStyle = {
      background: '#' + this.state.organizationObj.headerColor
    };
    */

    const accentHex = normalizeHexColor(this.state.organizationObj.buttonColor);
    const accentTextColor = getAccessibleTextColor(accentHex);
    const buttonOverrideStyle = {
      background: accentHex,
      color: accentTextColor,
    };

    const addPaymentButton = this.renderAddPaymentButton();
    const projectPaymentStrings = languageObj.ProjectPaymentList || {};
    const searchPlaceholder = projectPaymentStrings.searchPlaceholder || 'Search transactions';
    const searchAriaLabel = projectPaymentStrings.searchAriaLabel || searchPlaceholder;

    const paymentModeLabels = projectPaymentStrings.paymentModeFilter;
    const paymentModeOptions = [
      { value: '', label: paymentModeLabels.all },
      ...['Stripe', 'PayMe', 'FPS', 'Manual', 'TapAndGo'].map(value => ({ value, label: paymentModeLabels[value] })),
    ];
    const statusFilterLabels = projectPaymentStrings.statusFilter || {};
    const statusOptions = [
      { value: '', label: statusFilterLabels.all || 'All Statuses' },
      { value: 'complete', label: statusFilterLabels.complete || 'Succeeded' },
      { value: 'error', label: statusFilterLabels.error || 'Failed' },
      { value: 'expired', label: statusFilterLabels.expired },
      { value: 'processing', label: statusFilterLabels.processing || 'Processing' },
      { value: 'pending_for_confirmation', label: statusFilterLabels.pending_for_confirmation || 'Pending' },
      { value: 'scheduled', label: statusFilterLabels.scheduled || 'Scheduled' },
      { value: 'reauthenticate', label: statusFilterLabels.reauthenticate || 'Re-authenticate' },
      { value: 'refund', label: statusFilterLabels.refund || 'Refunded' },
      { value: 'dispute', label: statusFilterLabels.dispute || 'Disputed' },
    ];

    const orgPlanLower = (this.state.organizationObj && this.state.organizationObj.currentPlan)
      ? this.state.organizationObj.currentPlan.toLowerCase()
      : '';
    const canUseFilters = orgPlanLower === 'pro' || orgPlanLower === 'premium';
    const filterUpgradeHint = projectPaymentStrings.filterUpgradeHint || 'Upgrade to Pro to use filters';

    const sortLabels = projectPaymentStrings.sortOptions || {};
    const sortFieldOptions = [
      { value: 'updatedAt', label: sortLabels.updatedAt || 'Recent Activity' },
      { value: 'organizationReceiptId', label: sortLabels.organizationReceiptId || 'Receipt Number' },
      { value: 'donorName', label: sortLabels.donorName || 'Donor Name', disabled: !canUseFilters },
      { value: 'amount', label: sortLabels.amount || 'Amount', disabled: !canUseFilters },
    ];

    const sortOrderLabels = projectPaymentStrings.sortOrder || {};
    const isDateSort = this.state.paymentSortField === 'updatedAt';
    const isAmountSort = this.state.paymentSortField === 'amount';
    const isReceiptIdSort = this.state.paymentSortField === 'organizationReceiptId';

    let sortOrderOptions;
    if (isDateSort) {
      sortOrderOptions = [
        { value: 'desc', label: sortOrderLabels.newest || 'Newest First' },
        { value: 'asc', label: sortOrderLabels.oldest || 'Oldest First' },
      ];
    } else if (isAmountSort) {
      sortOrderOptions = [
        { value: 'desc', label: sortOrderLabels.highToLow || 'High to Low' },
        { value: 'asc', label: sortOrderLabels.lowToHigh || 'Low to High' },
      ];
    } else {
      sortOrderOptions = [
        { value: 'asc', label: sortOrderLabels.aToZ || 'A - Z' },
        { value: 'desc', label: sortOrderLabels.zToA || 'Z - A' },
      ];
    }

    const filterSortLabel = projectPaymentStrings.filterAndSort || 'Filter & Sort';
    const filterCardStrings = projectPaymentStrings.filterCard || {};
    const filterCardActiveMetaTemplate = filterCardStrings.activeMeta || '{n} active · tap × to remove';
    const filterCardClearAllText = filterCardStrings.clearAll || 'Clear all';
    const filterCardAddFilterText = filterCardStrings.addFilter || 'Add filter';

    const renderToolbar = () => (
      <div className="AccountOverview-toolbar">
        {addPaymentButton}

        <div className="AccountOverview-searchWrapper">
          <img
            src={searchIcon}
            alt=""
            aria-hidden="true"
            className="AccountOverview-searchIcon"
          />
          <input
            type="search"
            className="AccountOverview-searchInput"
            placeholder={searchPlaceholder}
            aria-label={searchAriaLabel}
            value={this.state.paymentSearchTerm}
            onChange={this.handlePaymentSearchChange}
          />
        </div>
      </div>
    );

    const statusLabel = projectPaymentStrings.statusLabel || 'Status';
    const sortByLabel = projectPaymentStrings.sortByLabel || 'Sort by';
    const orderLabel = projectPaymentStrings.orderLabel || 'Order';
    const transactionTypeLabel = projectPaymentStrings.transactionTypeLabel || 'Type';
    const dateRangeStrings = projectPaymentStrings.dateRange || {};
    const dateRangeLabel = dateRangeStrings.label || 'Date range';
    const dateFieldLabels = dateRangeStrings.dateField || {};
    const dateFieldOptions = [
      { value: 'scheduledDate', label: dateFieldLabels.scheduledDate || 'Transaction date' },
      { value: 'createdAt', label: dateFieldLabels.createdAt || 'Data entry date' },
    ];
    const datePresetLabels = dateRangeStrings.preset || {};
    const datePresetOptions = [
      { value: '', label: datePresetLabels.none || 'None' },
      { value: '7d', label: datePresetLabels.last7 || '7 days' },
      { value: '30d', label: datePresetLabels.last30 || '30 days' },
      { value: 'custom', label: datePresetLabels.custom || 'Custom' },
    ];
    const datePickerPlaceholder = dateRangeStrings.pickerPlaceholder || 'Pick a date or date range';
    const dateFieldLabel = dateRangeStrings.fieldLabel || 'Filter by';
    const activeDateFieldOpt = dateFieldOptions.find((o) => o.value === this.state.paymentDateField);

    const formatChipDate = (d) => {
      if (!d) return '';
      const date = new Date(d);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${y}/${m}/${day}`;
    };

    const transactionTypeFilterLabels = projectPaymentStrings.transactionTypeFilter || {};
    const transactionTypeOptions = isReceiptIdSort
      ? [
        { value: 'donation', label: transactionTypeFilterLabels.donation || 'Donation' },
        { value: 'payment', label: transactionTypeFilterLabels.payment || 'Payment' },
      ]
      : [
        { value: '', label: transactionTypeFilterLabels.all || 'All Types' },
        { value: 'donation', label: transactionTypeFilterLabels.donation || 'Donation' },
        { value: 'payment', label: transactionTypeFilterLabels.payment || 'Payment' },
      ];

    const activeStatusOpt = this.state.paymentStatusFilter
      ? statusOptions.find((o) => o.value === this.state.paymentStatusFilter)
      : null;
    const activeTypeOpt = this.state.paymentTransactionTypeFilter
      ? transactionTypeOptions.find((o) => o.value === this.state.paymentTransactionTypeFilter)
      : null;
    const currentSortOpt = sortFieldOptions.find((o) => o.value === this.state.paymentSortField);
    const currentOrderOpt = sortOrderOptions.find((o) => o.value === this.state.paymentSortOrder);

    const filterChips = [];
    const activeMode = paymentModeOptions.find(option => option.value === this.state.paymentModeFilter);
    if (activeMode && activeMode.value) {
      filterChips.push({ key: 'paymentMode', label: `${paymentModeLabels.label}: ${activeMode.label}`,
        onRemove: () => this.handlePaymentModeFilterChange({ target: { value: '' } }) });
    }
    if (activeStatusOpt) {
      filterChips.push({
        key: 'status',
        label: `${statusLabel}: ${activeStatusOpt.label}`,
        onRemove: () => this.handlePaymentStatusFilterChange({ target: { value: '' } }),
      });
    }
    if (activeTypeOpt) {
      filterChips.push({
        key: 'transactionType',
        label: `${transactionTypeLabel}: ${activeTypeOpt.label}`,
        // Receipt Number sort always needs one type, so its chip is informational only.
        onRemove: isReceiptIdSort
          ? null
          : () => this.handlePaymentTransactionTypeFilterChange({ target: { value: '' } }),
      });
    }
    if (currentSortOpt) {
      const isDefault = this.state.paymentSortField === ORG_PAYMENT_LIST_SORT;
      filterChips.push({
        key: 'sort',
        label: `${sortByLabel}: ${currentSortOpt.label}`,
        onRemove: isDefault
          ? null
          : () => this.handlePaymentSortChange({ target: { value: ORG_PAYMENT_LIST_SORT } }),
      });
    }
    if (!isReceiptIdSort && canUseFilters && currentOrderOpt) {
      const isDefault = this.state.paymentSortOrder === 'desc';
      filterChips.push({
        key: 'order',
        label: `${orderLabel}: ${currentOrderOpt.label}`,
        onRemove: isDefault
          ? null
          : () => this.handlePaymentSortOrderChange({ target: { value: 'desc' } }),
      });
    }
    if (this.state.paymentDateRangeStart && this.state.paymentDateRangeEnd) {
      const rangeText = `${formatChipDate(this.state.paymentDateRangeStart)} → ${formatChipDate(this.state.paymentDateRangeEnd)}`;
      const prefix = activeDateFieldOpt ? activeDateFieldOpt.label : dateRangeLabel;
      filterChips.push({
        key: 'dateRange',
        label: (
          <Fragment>
            {prefix}:{' '}
            <span className="AccountOverview-filterActiveChipNoWrap">{rangeText}</span>
          </Fragment>
        ),
        onRemove: () => this.handlePaymentDateRangePresetChange(''),
      });
    }
    const removableCount = filterChips.filter((c) => c.onRemove).length;
    const isExpanded = !!this.state.filterPanelExpanded;
    const cardActiveMetaText = filterCardActiveMetaTemplate.replace('{n}', String(removableCount));

    const handleCardKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleFilterPanel();
      }
    };

    const renderChipOptions = (options, currentValue, onSelect) => (
      <div className="AccountOverview-filterChipRow" role="radiogroup">
        {options.map((opt) => {
          const selected = currentValue === opt.value;
          const disabled = !!opt.disabled;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled}
              disabled={disabled}
              title={disabled ? filterUpgradeHint : undefined}
              className={`AccountOverview-filterChipOption${selected ? ' AccountOverview-filterChipOption--selected' : ''}${disabled ? ' AccountOverview-filterChipOption--disabled' : ''}`}
              onClick={disabled ? undefined : () => onSelect(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );

    const renderFilterSort = () => (
      <div
        className={`AccountOverview-filterCard${isExpanded ? ' AccountOverview-filterCard--expanded' : ''}${removableCount > 0 ? ' AccountOverview-filterCard--active' : ''}`}
      >
        <div
          className="AccountOverview-filterCardHeader"
          onClick={this.toggleFilterPanel}
          onKeyDown={handleCardKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
        >
          <div className={`AccountOverview-filterIconBadge${removableCount > 0 ? ' AccountOverview-filterIconBadge--active' : ''}`} aria-hidden="true">
            <svg viewBox="0 0 18 18" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M2 4h14" />
              <path d="M4 9h10" />
              <path d="M7 14h4" />
            </svg>
          </div>

          <div className="AccountOverview-filterCardSummary">
            {removableCount > 0 && (
              <div className="AccountOverview-filterCardMetaRow">
                <span className="AccountOverview-filterCardMeta">{cardActiveMetaText}</span>
                <button
                  type="button"
                  className="AccountOverview-filterClearAll"
                  onClick={(e) => { e.stopPropagation(); this.clearAllFilters(); }}
                >
                  {filterCardClearAllText}
                </button>
              </div>
            )}
            <div className="AccountOverview-filterChipFlow">
              {filterChips.map((chip) => (
                <span
                  key={chip.key}
                  className={`AccountOverview-filterActiveChip${chip.onRemove ? '' : ' AccountOverview-filterActiveChip--info'}`}
                >
                  <span className="AccountOverview-filterActiveChipLabel">{chip.label}</span>
                  {chip.onRemove && (
                    <button
                      type="button"
                      className="AccountOverview-filterActiveChipRemove"
                      onClick={(e) => { e.stopPropagation(); chip.onRemove(); }}
                      aria-label={`Remove ${chip.label}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              <button
                type="button"
                className="AccountOverview-filterAddChip"
                onClick={(e) => { e.stopPropagation(); if (!isExpanded) this.toggleFilterPanel(); }}
              >
                + {filterCardAddFilterText}
              </button>
            </div>
          </div>

          <div className={`AccountOverview-filterCardChevron${isExpanded ? ' AccountOverview-filterCardChevron--expanded' : ''}`} aria-hidden="true">
            <svg viewBox="0 0 12 8" width="12" height="8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 1.5 L6 6.5 L11 1.5" />
            </svg>
          </div>
        </div>

        {isExpanded && (
          <div className="AccountOverview-filterPanel">
            <div className="AccountOverview-filterSection">
              <div className="AccountOverview-filterSectionLabel">{paymentModeLabels.label}</div>
              {renderChipOptions(
                paymentModeOptions,
                this.state.paymentModeFilter,
                (value) => this.handlePaymentModeFilterChange({ target: { value } })
              )}
            </div>
            <div className="AccountOverview-filterSection">
              <div className="AccountOverview-filterSectionLabel">{statusLabel}</div>
              {renderChipOptions(
                statusOptions,
                this.state.paymentStatusFilter,
                (value) => this.handlePaymentStatusFilterChange({ target: { value } })
              )}
            </div>

            <div className="AccountOverview-filterSection">
              <div className="AccountOverview-filterSectionLabel">{transactionTypeLabel}</div>
              {renderChipOptions(
                transactionTypeOptions,
                this.state.paymentTransactionTypeFilter,
                (value) => this.handlePaymentTransactionTypeFilterChange({ target: { value } })
              )}
            </div>

            <div className="AccountOverview-filterSection">
              <div className="AccountOverview-filterSectionLabel">{dateRangeLabel}</div>
              <div className="AccountOverview-filterSubLabel">{dateFieldLabel}</div>
              {renderChipOptions(
                dateFieldOptions,
                this.state.paymentDateField,
                (value) => this.handlePaymentDateFieldChange(value)
              )}
              {renderChipOptions(
                datePresetOptions,
                this.state.paymentDateRangePreset,
                (value) => this.handlePaymentDateRangePresetChange(value)
              )}
              {this.state.paymentDateRangePreset === 'custom' && (
                <div className="AccountOverview-filterDatePickerWrap">
                  <DatePicker
                    selectsRange
                    startDate={this.state.paymentDateRangeStart}
                    endDate={this.state.paymentDateRangeEnd}
                    maxDate={new Date()}
                    onChange={this.handlePaymentDateRangeChange}
                    dateFormat="yyyy/MM/dd"
                    isClearable
                    placeholderText={datePickerPlaceholder}
                  />
                </div>
              )}
            </div>

            <div className="AccountOverview-filterSection">
              <div className="AccountOverview-filterSectionLabel">{sortByLabel}</div>
              {renderChipOptions(
                sortFieldOptions,
                this.state.paymentSortField,
                (value) => this.handlePaymentSortChange({ target: { value } })
              )}
            </div>

            {!isReceiptIdSort && canUseFilters && (
              <div className="AccountOverview-filterSection">
                <div className="AccountOverview-filterSectionLabel">{orderLabel}</div>
                {renderChipOptions(
                  sortOrderOptions,
                  this.state.paymentSortOrder,
                  (value) => this.handlePaymentSortOrderChange({ target: { value } })
                )}
              </div>
            )}

            {removableCount > 0 && (
              <div className="AccountOverview-filterPanelFooter">
                <button
                  type="button"
                  className="AccountOverview-filterPanelClearAll"
                  onClick={this.clearAllFilters}
                >
                  {filterCardClearAllText}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );

    const qrUrl =
      "https://" + this.state.organizationObj.subDomain + "." + MAIN_DOMAIN;

    const managerOpen = this.state.showProjectTable;

    return (
      <div
        className={`AccountOverview-view${
          managerOpen
            ? ''
            : this.state.mobileTab === 'transactions'
              ? ' AccountOverview-view--mTxns'
              : ' AccountOverview-view--mProjects'
        }`}
      >
        {['local', 'dev'].includes(ENVIRONMENT) && new URLSearchParams(window.location.search).has('debug') && (() => {
          const debugSections = {
            props: { label: 'Props', data: (() => {
              try { return JSON.parse(JSON.stringify(this.props)); } catch (e) { return { error: 'Circular reference: ' + e.message }; }
            })()},
            organizationObj: { label: 'Organization', data: this.state.organizationObj },
            balance: { label: 'Balance', data: this.state.balance },
            missions: { label: 'Missions', data: this.state.missions },
            paymentsList: { label: 'Payments List', data: this.state.selectedProject.paymentsList },
            selectedProject: { label: 'Selected Project', data: {
              loadingList: this.state.selectedProject.loadingList,
              hasMore: this.state.selectedProject.hasMore,
              searchTerm: this.state.selectedProject.searchTerm,
              lastId: this.state.selectedProject.lastId,
              lastUpdatedAt: this.state.selectedProject.lastUpdatedAt,
              isFetchingMore: this.state.selectedProject.isFetchingMore,
              paymentsCount: this.state.selectedProject.paymentsList ? this.state.selectedProject.paymentsList.length : 0,
            }},
            paymentDetail: { label: 'Payment Detail', data: this.state.paymentDetail },
            stripeStatus: { label: 'Stripe Status', data: {
              kyc: this.state.kyc,
              cardPaymentsStatus: this.state.cardPaymentsStatus,
              transfersStatus: this.state.transfersStatus,
              charges_enabled: this.state.charges_enabled,
              payouts_enabled: this.state.payouts_enabled,
            }},
            fullState: { label: 'Full State', data: (() => {
              try { return JSON.parse(JSON.stringify(this.state)); } catch (e) { return { error: 'Circular reference: ' + e.message }; }
            })()},
          };
          const activeData = debugSections[this.state.debugActiveSection] || {};
          return (
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
              background: '#1a1a2e',
              borderTop: '2px solid #e94560',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#eee',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                flexWrap: 'wrap',
              }}>
                <span style={{ color: '#e94560', fontWeight: 'bold' }}>DEBUG</span>
                <button
                  onClick={() => this.setState(prev => ({ debugPanelOpen: !prev.debugPanelOpen }))}
                  style={{
                    background: this.state.debugPanelOpen ? '#e94560' : '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 10px',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                  }}
                >
                  {this.state.debugPanelOpen ? 'Collapse' : 'Expand'}
                </button>
                {this.state.debugPanelOpen && Object.keys(debugSections).map(key => (
                  <button
                    key={key}
                    onClick={() => this.setState({ debugActiveSection: key })}
                    style={{
                      background: this.state.debugActiveSection === key ? '#e94560' : '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                    }}
                  >
                    {debugSections[key].label}
                  </button>
                ))}
              </div>
              {this.state.debugPanelOpen && (
                <pre style={{
                  margin: 0,
                  padding: '8px 12px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  background: '#16213e',
                  color: '#a8dadc',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}>
                  {JSON.stringify(activeData.data, null, 2)}
                </pre>
              )}
            </div>
          );
        })()}
        <div className="AccountOverview-view1">
          <div className="AccountOverview-headerContainer">
            <div
              className={
                PLATFORM === "plug"
                  ? "AccountOverview-PlugLogo desktopOnly"
                  : "AccountOverview-CheckPlusLogo desktopOnly"
              }
            />
            <div className="AccountOverview-mobileNav">
              <div
                className={`AccountOverview-menuContainer ${this.state.menuOpen ? 'active' : ''}`}
                onClick={this.toggleMenu}
              >
                <div className="AccountOverview-menuIcnContainer">
                  <div className="AccountOverview-menuIcon">
                    <div></div>
                  </div>
                </div>
              </div>

              <div
                className={
                  PLATFORM === "plug"
                    ? "AccountOverview-PlugLogo mobileOnly"
                    : "AccountOverview-CheckPlusLogo mobileOnly"
                }
              />

              <div className="AccountOverview-languageSelector mobileOnly">
                <div className="AccountOverview-globe mobileOnly" />
                {renderOrgLanguageSelector()}
              </div>
            </div>

            <div className="AccountOverview-navBarContainer">
              <div
                className="AccountOverview-homeContainer"
                onClick={this.onHomeBtnClick}
              >
                <div className="AccountOverview-homeIcnContainer">
                  <div className="AccountOverview-homeIcn"></div>
                </div>
              </div>

              <div
                className="AccountOverview-settingsContainer"
                data-testid="accountoverview-org-settings-btn"
                onClick={this.orgSettingsBtn}
              >
                <div className="AccountOverview-settingsIcnContainer">
                  <div className="AccountOverview-settingsIcon" />
                </div>
              </div>

              <a
                href="https://intercom.help/checkplus/en"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="AccountOverview-faqContainer">
                  <div className="AccountOverview-faqIcnContainer">
                    <div className="AccountOverview-faqIcon" />
                  </div>
                </div>
              </a>

              <div
                className="AccountOverview-logoutContainer"
                onClick={this.onLogoutClick}
              >
                <div className="AccountOverview-logoutIcnContainer">
                  <div className="AccountOverview-logoutIcon" />
                </div>
              </div>
              
              <div className="AccountOverview-languageSelector desktopOnly">
                <div className="AccountOverview-globe"></div>
                {renderOrgLanguageSelector()}
              </div>
            </div>
          </div>
        </div>

        <div className={`AccountOverview-overlay ${this.state.menuOpen ? 'visible' : ''}`} onClick={this.toggleMenu}></div>

        <div className={`AccountOverview-leftSidebar ${!this.state.menuOpen ? 'collapsed' : ''}`}>
          <div className="AccountOverview-mobileNavMenu">
            <div className="AccountOverview-navItems">
              <div
                className="AccountOverview-homeContainer"
                onClick={this.onHomeBtnClick}
              >
                <div className="AccountOverview-homeIcnContainer">
                  <div className="AccountOverview-homeIcn"></div>
                </div>
              </div>

              <div
                className="AccountOverview-settingsContainer"
                onClick={this.orgSettingsBtn}
              >
                <div className="AccountOverview-settingsIcnContainer">
                  <div className="AccountOverview-settingsIcon" />
                </div>
              </div>

              <a
                href="https://intercom.help/checkplus/en"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="AccountOverview-faqContainer">
                  <div className="AccountOverview-faqIcnContainer">
                    <div className="AccountOverview-faqIcon" />
                  </div>
                </div>
              </a>

              <div
                className="AccountOverview-logoutContainer"
                onClick={this.onLogoutClick}
              >
                <div className="AccountOverview-logoutIcnContainer">
                  <div className="AccountOverview-logoutIcon" />
                </div>
              </div>
            </div>
          </div>

        </div>

        <div
          className={`AccountOverview-view2${
            managerOpen ? " AccountOverview-view2--hidden" : ""
          }`}
        >
          <div className="AccountOverview-fundsandItems">
            <div className="AccountOverview-fundsContainer">
              <div className="AccountOverview-sidebarContainer">
                {this.state.kyc > 0 ? (
                  <div
                    className="AccountOverview-kycNotification"
                    onClick={this.navigateToStripe}
                  >
                    <div className="AccountOverview-missingKycData" />
                    <div className=" AccountOverview-kycRed">
                      <span className="AccountOverview-actionRequired">
                        {languageObj.actionRequired}
                      </span>
                      <br />
                      <span className="AccountOverview-actionDetails">
                        {languageObj.kycActionDetails}
                      </span>
                    </div>
                    <div className="AccountOverview-kycArrow" />
                  </div>
                ) : null}

                {/* Payout setup was deferred and never finished (CP-304).
                    There is no Connected Account, so this is the one state
                    that is genuinely resumable — it links into setup. */}
                {payoutNoticeStateOf(this.state) === "deferred" ? (
                  <div
                    className="AccountOverview-stripeNotification"
                    onClick={this.navigateToStripe}
                  >
                    <div className="AccountOverview-stripe-logo-white-notification" />
                    <div className=" AccountOverview-stripePurple">
                      <span className="AccountOverview-actionRequired ">
                        {languageObj.actionRequired}
                      </span>
                      <br />
                      <span className="AccountOverview-actionDetails">
                        {languageObj.deferredPayoutDetails}
                      </span>
                    </div>
                    <div className="AccountOverview-stripeArrow" />
                  </div>
                ) : null}

                {/* An account exists but Stripe has both capabilities off —
                    suspended, rejected or under review. Nothing here is
                    resumable by the org, so it stays the passive notice it
                    has always been rather than an action it cannot take. */}
                {payoutNoticeStateOf(this.state) === "disabled" ? (
                  <div className="AccountOverview-disabledNotification">
                    <div className="AccountOverview-stripe-logo-white-notification-disabled" />
                    <div className=" AccountOverview-disabledGrey">
                      <span className="AccountOverview-disabledTitle ">
                        {languageObj.paymentsAndPayouts}
                      </span>
                      <br />
                      <span className="AccountOverview-disabledDescription">
                        {languageObj.disabled}
                      </span>
                    </div>
                  </div>
                ) : null}

                {this.state.charges_enabled === true &&
                this.state.payouts_enabled === false ? (
                    <div className="AccountOverview-disabledNotification">
                      <div className="AccountOverview-stripe-logo-white-notification-disabled" />
                      <div className=" AccountOverview-disabledGrey">
                        <span className="AccountOverview-disabledTitle ">
                          {languageObj.payouts}
                        </span>
                        <br />
                        <span className="AccountOverview-disabledDescription">
                          {languageObj.disabled}
                        </span>
                      </div>
                    </div>
                  ) : null}

                {this.state.charges_enabled === false &&
                this.state.payouts_enabled === true ? (
                    <div className="AccountOverview-disabledNotification">
                      <div className="AccountOverview-stripe-logo-white-notification-disabled" />
                      <div className=" AccountOverview-disabledGrey">
                        <span className="AccountOverview-disabledTitle ">
                          {languageObj.payments}
                        </span>
                        <br />
                        <span className="AccountOverview-disabledDescription">
                          {languageObj.disabled}
                        </span>
                      </div>
                    </div>
                  ) : null}

                {this.state.cardPaymentsStatus === "unrequested" &&
                this.state.transfersStatus === "unrequested" ? (
                    <div
                      className="AccountOverview-stripeNotification"
                      onClick={this.navigateToStripe}
                    >
                      <div className="AccountOverview-stripe-logo-white-notification" />
                      <div className=" AccountOverview-stripePurple">
                        <span className="AccountOverview-actionRequired ">
                          {languageObj.actionRequired}
                        </span>
                        <br />
                        <span className="AccountOverview-actionDetails">
                          {languageObj.threeDSDetails}
                        </span>
                      </div>
                      <div className="AccountOverview-stripeArrow" />
                    </div>
                  ) : null}
                <div className="AccountOverview-widgetContainer">
                  <div className="AccountOverview-QrContainer">
                    <div className="AccountOverview-QrTop">
                      <div className="AccountOverview-QrCode">
                        <QRCodeCanvas
                          style={{
                            height: "100%",
                            width: "100%",
                          }}
                          value={qrUrl}
                        />
                      </div>
                    </div>
                    <div
                      className="AccountOverview-visitBtn"
                      onClick={() => window.open(qrUrl, "_blank")}
                    >
                      {languageObj.visitBtn}
                    </div>
                  </div>

                  <div className="AccountOverview-StripeAmount">
                    <div className="AmountOverview-amountGroup">
                      <div className="AccountOverview-stripe-logo-white" />
                      <div className="AccountOverviewSubtitle">
                        {languageObj.AccountOverviewSubtitle} <br />
                        {languageObj.hkd}
                      </div>
                      <div className="AccountOverviewtotalAmountReceived">
                        HK
                        <NumericFormat
                          decimalSeparator="."
                          displayType="text"
                          valueIsNumericString={true}
                          value={this.state.balance}
                          allowNegative={false}
                          thousandSeparator={true}
                          prefix={"$"}
                          fixedDecimalScale={true}
                          decimalScale={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {!managerOpen ? (
                  <Fragment>
                    {/* Desktop sidebar: Cards / List view toggle. List expands
                        into the table takeover. Hidden on mobile and while
                        reordering (the Back/Save toolbar takes over). */}
                    {!this.state.isFrameEdit
                      ? this.renderProjectViewTabs(languageObj)
                      : null}

                    {/* Mobile-only segmented control (<=750px): Transactions =
                        the transaction list, Projects = the inline manager card
                        view, List = the inline manager table view (with Manage
                        Images). Hidden on desktop. */}
                    <div
                      className="AccountOverview-mobileTabs mobileOnly"
                      role="tablist"
                      aria-label={languageObj.mobileTabsAriaLabel || 'View'}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={this.state.mobileTab === 'transactions'}
                        className={`AccountOverview-mobileTab${this.state.mobileTab === 'transactions' ? ' AccountOverview-mobileTab--active' : ''}`}
                        onClick={() => this.setMobileTab('transactions')}
                      >
                        {languageObj.mobileTabTransactions || 'Transactions'}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={this.state.mobileTab === 'projects'}
                        className={`AccountOverview-mobileTab${this.state.mobileTab === 'projects' ? ' AccountOverview-mobileTab--active' : ''}`}
                        onClick={() => this.setMobileTab('projects')}
                      >
                        {languageObj.mobileTabProjects || 'Projects'}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={this.state.mobileTab === 'list'}
                        className={`AccountOverview-mobileTab${this.state.mobileTab === 'list' ? ' AccountOverview-mobileTab--active' : ''}`}
                        onClick={() => this.setMobileTab('list')}
                      >
                        {languageObj.sidebarViewList || 'List'}
                      </button>
                    </div>

                    <div className="AccountOverview-mobileProjectPane">
                      {this.state.isMobile ? (
                        // Mobile: the Projects tab IS the full manager (Cards/
                        // Table, reorder, Manage Images, quick-edit), rendered
                        // inline instead of the desktop Expand-Projects takeover.
                        // "All Transactions" renders inside the manager body,
                        // just below the status filter (see renderProjectManager).
                        this.renderProjectManager(languageObj)
                      ) : (
                        <Fragment>
                          {/* Reorder swaps the bar for a Back/Save toolbar and
                              hides the nav — same behavior as mobile. */}
                          {this.state.isFrameEdit ? (
                            <div className="AccountOverview-projectManagerToolbar AccountOverview-projectManagerToolbar--reorder AccountOverview-sidebarReorderBar">
                              {this.renderReorderControls(languageObj)}
                            </div>
                          ) : (
                            <Fragment>
                              {this.renderCombinedActionBar(languageObj)}
                              {this.renderAllTransactionsNav(languageObj)}
                            </Fragment>
                          )}

                          {/* Reorder happens right here in the sidebar (no
                              takeover): during reorder the cards become
                              draggable and show drop indicators. */}
                          <div
                            className="AccountOverview-sidebarCards"
                            onDragLeave={
                              this.state.isFrameEdit
                                ? this.handleProjectDragLeaveList
                                : undefined
                            }
                            onDrop={
                              this.state.isFrameEdit
                                ? this.handleProjectDrop
                                : undefined
                            }
                            onDragOver={
                              this.state.isFrameEdit
                                ? (e) => {
                                  if (this.state.dragProjectId != null) e.preventDefault();
                                }
                                : undefined
                            }
                          >
                            {this.getFilteredMissions().map((mission, index) => (
                              <Fragment key={mission.id}>
                                {this.state.isFrameEdit &&
                                this.state.dropProjectIndex === index ? (
                                    <div className="AccountOverview-projectDropIndicator" />
                                  ) : null}
                                <ProjectDetails
                                  isFrameEdit={this.state.isFrameEdit}
                                  isDragging={
                                    String(this.state.dragProjectId) === String(mission.id)
                                  }
                                  mission={mission}
                                  progressBarColor={
                                    this.state.organizationObj.progressBarColor
                                  }
                                  onProjectClick={this.onProjectClicked}
                                  onProjectSettingClick={this.onProjectSettingClick}
                                  languageObj={languageObj.ProjectDetails}
                                  language={this.props.language}
                                  leftArrowBtn={this.leftArrowBtn}
                                  rightArrowBtn={this.rightArrowBtn}
                                  onDragStart={this.handleProjectDragStart}
                                  onDragOver={this.handleProjectDragOver}
                                  onDragEnd={this.handleProjectDragEnd}
                                  index={index}
                                />
                              </Fragment>
                            ))}
                            {this.state.isFrameEdit &&
                            this.state.dropProjectIndex === this.getFilteredMissions().length ? (
                                <div className="AccountOverview-projectDropIndicator" />
                              ) : null}
                          </div>

                          {this.getFilteredMissions().length === 0 &&
                          this.state.missions.length > 0 &&
                          !this.state.isLoadingAllProjects ? (
                              <div className="AccountOverview-projectEmptyState">
                                {languageObj.projectEmptyState || 'No projects match your search.'}
                              </div>
                            ) : null}

                          {this.renderFinalCard()}

                          <div className="AccountOverview-spacing"></div>
                        </Fragment>
                      )}
                    </div>
                  </Fragment>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`AccountOverview-view3${
            managerOpen && !this.state.isMobile ? " AccountOverview-view3--expanded" : ""
          }`}
        >
          <div className="AccountOverview-renderProjectPaymentList">
            {managerOpen && !this.state.isMobile ? (
              // Desktop takeover only — on mobile the manager renders inline in
              // the Projects tab (guarded so it never double-renders).
              this.renderProjectManager(languageObj)
            ) : this.state.showDashboard ? (
              <div className="AccountOverview-dashboardContainer">
                {renderToolbar()}
                {this.renderStatsDashboard(buttonOverrideStyle, {
                  ...(languageObj.ProjectPaymentList || {}),
                  totalVolume:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.totalVolume) || 'Total Volume',
                  transactions:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.transactions) || 'transactions',
                  avgAmount:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.avgAmount) || 'Average Amount',
                  successRate:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.successRate) || 'success rate',
                  donations:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.donations) || 'Donations',
                  payments:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.payments) || 'Payments',
                  count:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.count) || 'count',
                  topProjects:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.topProjects) || 'Top Projects',
                  all:
                    (languageObj.ProjectPaymentList &&
                      languageObj.ProjectPaymentList.all) || 'All',
                })}
              </div>
            ) : (
              this.renderProjectPaymentList(
                languageObj.ProjectPaymentList,
                renderToolbar(),
                renderFilterSort()
              )
            )}
          </div>
        </div>

        {this.state.paymentDetail && (
          <div className="AccountOverview-sideSheetBackdrop" onClick={this.closeSideSheet} />
        )}
        <div className={`AccountOverview-sideSheet${this.state.paymentDetail ? ' AccountOverview-sideSheet--open' : ''}`}>
          {this.renderPaymentDetail(this, (languageObj.ProjectPaymentList || {}).PaymentDetail)}
        </div>
      </div>
    );
  }
}

AccountOverview.propTypes = {
  orgLogout: PropTypes.func,
  language: PropTypes.string,
  languageObj: PropTypes.object,
};

export default AccountOverview;
